/**
 * Speisekarten-Import (Spec 06, F3 Review-UI): Upload/Text -> KI-Parse -> menschlicher
 * Review (nie Auto-Commit) -> transaktionaler Katalog-Commit.
 *
 * Der Review ist das Herzstueck: extrahierte Felder editierbar, Konfidenz-/Sanity-Hinweise
 * sichtbar (gelb), Allergene per LMIV-Toggle, Pflicht-Bestaetigung „Allergene geprueft"
 * vor dem Commit. Beim Commit wird der (ggf. korrigierte) Stand als parsedPayload gesendet.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch, toApiError } from "../lib/api";
import { useApi, apiMutate } from "../hooks/useApi";
import { openAuthenticatedFile, fileToBase64 } from "../lib/files";
import type { CatalogCategory, CatalogItem, CommitSummary, MenuCard, MenuCardType, MenuImport, ParsedMenu, PreisLayout, SanityFlag, SpellSuggestion, AllergenSuggestion } from "../ui/types";
import { Icon, ErrorNote, Modal, Button, Input, Card, Rueckweg, Select, useBestaetigung, EmptyState, Checkbox, Radio, Textarea, ButtonOptik } from "../ui/components";
import { parseGermanEurosToCents } from "../ui/format";
import { describeSpellChange } from "../ui/spell-change";
import { kartennameAusDateiname, findeKartenNamenstreffer } from "../lib/karten-namenstreffer";
import { findeAehnlichePosition } from "../lib/positions-aehnlichkeit";
import { IMPORT_SPRACHEN, uebernehmeUebersetzungen, preissprungProzent, type ImportSprache } from "../lib/import-kette";

// LMIV-Hauptallergene + Diät-Flags: EINE gemeinsame Quelle für alle Flächen (ui/lmiv).
import { LMIV, DIETARY as DIET, lmivLabel } from "../ui/lmiv";
import { TranslationPairReview, type TranslationPair } from "../ui/translation-review";
import { ImportPreisHinweis } from "../ui/ImportPreisHinweis";
import { KartenartWahl, KartenartAufforderung, lesenAufforderungWeg, type KartenartAntwort } from "../ui/KartenartWahl";
import { NichtEingeleseneDokumente, type NichtEingelesenesDokument } from "../ui/NichtEingeleseneDokumente";

/** Sprachname fuer die Erkennungs-Meldung („die englische Version von …"). */
const LANG_NAME: Record<string, string> = {
  en: "englische",
  it: "italienische",
  fr: "französische",
  es: "spanische",
  de: "deutsche",
};

// M3-Fix: hartes Client-Größenlimit (Spiegel des Server-Limits MAX_BYTES_PER_FILE = 8 MB).
// Übergroße Dateien werden gar nicht erst Base64-kodiert/hochgeladen (kein 413-Roundtrip).
const MAX_FILE_BYTES = 8 * 1024 * 1024;

// design-komponenten(p2): Sentinel fuer die Ziel-Karte-Selects unten — die urspruengliche
// <option value=""> war dort eine ECHTE, jederzeit erneut waehlbare Option ("noch keine Karte
// gewaehlt"), keine reine Platzhalter-Deko. Radix Select.Item erlaubt keinen leeren value-String
// (Muster: MANDANTEN_STANDARD aus NachschlagenTabs.tsx). Lokal in "" zurueckuebersetzt, bevor der
// Wert die Komponente verlaesst — Verhalten (inkl. erneutem Zuruecksetzen ueber das Dropdown)
// unveraendert.
const KEINE_KARTE = "__keine-karte__";

/**
 * Zeigt IMMER die reale Fehlermeldung (Server-`message` bzw. der geworfene Fehler) —
 * keine erfundenen/ausgedachten Texte. Nur wenn wirklich keine Meldung vorliegt, greift
 * ein neutraler, ehrlicher Fallback plus (falls vorhanden) der rohe Fehlercode.
 */
function importError(message: string | null | undefined, code?: string | null): string {
  if (message && message.trim()) return message.trim();
  if (code && code.trim()) return `Fehler: ${code.trim()}`;
  return "Analyse fehlgeschlagen.";
}

// Kanonische Anzeige: deutsches Format mit Komma-Dezimaltrenner ("8,90").
function centsToEuros(c: number | null): string {
  return c == null ? "" : (c / 100).toFixed(2).replace(".", ",");
}
// Parsen ueber den geteilten Format-Util (deutsches Format: Komma-Dezimal, optional Punkt-Tausender)
// statt naivem parseFloat — sonst wuerde "1.500" faelschlich zu 1,50 € statt 1.500 €.
function eurosToCents(input: string): number | null {
  return parseGermanEurosToCents(input);
}

/**
 * C1-Fix: Preis-Eingabe im Review-Schritt.
 * Waehrend des Tippens wird der ROH-String gehalten (kein reformat-on-keystroke) — sonst
 * schrieb der onChange bei jedem Tastendruck den zu Cents geparsten & zurueckformatierten
 * Wert ins Feld, setzte den Caret zurueck und mehrstellige Eingaben zerstoerten sich
 * ("8,90" -> "8.01"). Normalisiert/committet wird erst beim Verlassen des Felds (onBlur).
 * Muster gespiegelt vom Angebots-Builder (useBufferedField).
 */
function PriceInput({
  cents,
  priceDisplay,
  onCommit,
}: {
  cents: number | null;
  priceDisplay: string | null | undefined;
  onCommit: (cents: number | null) => void;
}) {
  const canonical = centsToEuros(cents);
  const [raw, setRaw] = useState(canonical);
  const focused = useRef(false);
  // Externe Aenderungen (z. B. KI-Reparse) uebernehmen — aber nie waehrend der Nutzer tippt.
  useEffect(() => {
    if (!focused.current) setRaw(canonical);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonical]);
  return (
    <Input
      className="pr-7 text-right"
      inputMode="decimal"
      placeholder={priceDisplay ?? "0,00"}
      value={raw}
      onFocus={() => {
        focused.current = true;
      }}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={() => {
        focused.current = false;
        const c = eurosToCents(raw);
        onCommit(c);
        setRaw(centsToEuros(c));
      }}
    />
  );
}

type Step = "upload" | "parsing" | "review" | "done";
type SourceTab = "file" | "text";

// Datei-Größe menschenlesbar (deutsches Komma) für die Datei-Chip-Anzeige.
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1).replace(".", ",")} MB`;
}

// Pillen-Tab im App-Stil (gespiegelt von catalog.tsx) — für „PDF oder Foto" / „Text einfügen".
function tabClass(active: boolean): string {
  return `inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-label-md text-label-md transition-colors ${
    active
      ? "bg-primary text-on-primary"
      : "bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest dark:bg-white/[0.08] dark:text-secondary-fixed-dim"
  }`;
}

/** Ergebnis-Zeile im Fertig-Screen (Design 15e): Icon + fette Zeile + Unterzeile. */
function ResultRow({ icon, title, sub }: { icon: string; title: string; sub: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-container-high dark:bg-white/[0.08]">
        <Icon name={icon} className="text-[16px] text-on-surface-variant" />
      </span>
      <span className="min-w-0">
        <span className="block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{title}</span>
        <span className="block font-body-sm text-[12px] text-on-surface-variant/80">{sub}</span>
      </span>
    </div>
  );
}
const LANG_LABEL_DONE: Record<string, string> = { en: "englische", it: "italienische", fr: "französische", es: "spanische" };

/** Rechtschreib-Chip: EIN Klick uebernimmt den KI-Vorschlag - nie automatisch. */
function SpellChip({ corrected, onApply }: { corrected: string; onApply: () => void }) {
  return (
    <button
      type="button"
      className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-secondary/40 bg-secondary/10 px-2 py-1 text-left font-body-sm text-[12px] text-secondary hover:bg-secondary/20 dark:text-[#b8ae9e]"
      title="Rechtschreib-Vorschlag der KI übernehmen"
      onClick={onApply}
    >
      <Icon name="spellcheck" className="shrink-0 text-[14px]" />
      <span className="truncate">„{corrected}"</span>
      <span className="shrink-0 font-semibold">Übernehmen</span>
    </button>
  );
}

/**
 * Schritt-Indikator (Design 15, storia-Review): IMMER vier Schritte - Hochladen,
 * Zuordnen, Pruefen, Fertig. Aktiver Schritt = roter gefuellter Kreis, erledigte =
 * gruenes Haekchen, offene = neutraler Kreis. Zentriert unter dem Seitentitel.
 * Bei deutschen Karten ohne Zuordnungs-Frage gilt "Zuordnen" als still erledigt.
 */
function Stepper({ phase }: { phase: 1 | 2 | 3 | 4 }) {
  const labels = ["Hochladen", "Zuordnen", "Prüfen", "Fertig"] as const;
  return (
    <div className="flex items-center justify-center gap-2 font-label-md text-label-md">
      {labels.map((label, i) => {
        const n = (i + 1) as 1 | 2 | 3 | 4;
        const done = n < phase || (phase === 4 && n === 4 && false);
        const active = n === phase;
        return (
          <div key={label} className="flex items-center gap-2">
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-semibold ${
                done
                  ? "bg-[#F0EAE0] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"
                  : active
                    ? "bg-primary text-on-primary"
                    : "border border-outline-variant/60 text-on-surface-variant dark:border-outline/40"
              }`}
            >
              {done ? <Icon name="check" className="text-[14px]" /> : n}
            </span>
            <span className={active ? "font-semibold text-on-background dark:text-inverse-on-surface" : done ? "text-on-background dark:text-inverse-on-surface" : "text-on-surface-variant"}>
              {label}
            </span>
            {i < labels.length - 1 && <span className="mx-1 h-px w-10 bg-outline-variant/50" />}
          </div>
        );
      })}
    </div>
  );
}

/** Analyse-Moment (17b): DREI echte Schritte, jeder ein realer KI-Aufruf (kein Timer).
 *  Bewusst „Positionen" statt „Gerichte" (Antoine, 31.07.2026): Ueber diese Seite laufen auch
 *  Getraenkekarten — sein Screenshot zeigte „Karte lesen — Gerichte …", waehrend gerade
 *  STORIA_Getraenkekarte_2025 gelesen wurde. */
const ANALYSIS_STAGES: { icon: string; label: string; unit: string }[] = [
  { icon: "restaurant_menu", label: "Karte lesen — Positionen, Preise, Kategorien", unit: "Positionen" },
  { icon: "spellcheck", label: "Rechtschreibung prüfen", unit: "Vorschläge" },
  { icon: "verified", label: "Allergene analysieren (LMIV)", unit: "Vorschläge" },
  // 150 P2: Übersetzung läuft ohne Zwischenklick mit — je Sprache ein eigener, wiederholbarer Schritt.
  { icon: "translate", label: "Übersetzen — Englisch", unit: "Texte" },
  { icon: "translate", label: "Übersetzen — Italienisch", unit: "Texte" },
  { icon: "translate", label: "Übersetzen — Französisch", unit: "Texte" },
];
const ERSTE_UEBERSETZUNGS_STUFE = 3;
const SPRACH_KURZ: Record<ImportSprache, string> = { en: "EN", it: "IT", fr: "FR" };

/**
 * 18a Inline-Wortdiff: zeigt den Satz EINMAL und markiert nur die geänderte Stelle.
 * Liefert gemeinsamen Präfix/Suffix + die entfernten/neuen Wörter der Änderungsstelle.
 */
function wordDiff(orig: string, corrected: string): { prefix: string[]; removed: string[]; added: string[]; suffix: string[]; label: string } {
  const a = orig.trim().split(/\s+/).filter(Boolean);
  const b = corrected.trim().split(/\s+/).filter(Boolean);
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  let j = 0;
  while (j < a.length - i && j < b.length - i && a[a.length - 1 - j] === b[b.length - 1 - j]) j++;
  const removed = a.slice(i, a.length - j);
  const added = b.slice(i, b.length - j);
  const label = removed.length > 0 && added.length === 0 ? "Doppeltes Wort entfernt"
    : added.length > 0 && removed.length === 0 ? "Wort ergänzt"
    : "Tippfehler korrigiert";
  return { prefix: a.slice(0, i), removed, added, suffix: a.slice(a.length - j), label };
}

export default function MenuImportPage() {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("upload");
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [text, setText] = useState("");
  // Redesign 9a: Datei/Text als Tabs statt gestapelt.
  const [srcTab, setSrcTab] = useState<SourceTab>("file");
  // Ein einziges verstecktes File-Input, per Ref aus Dropzone UND den Recovery-Aktionen (9b) ausgelöst.
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Drag-anywhere: sobald irgendwo auf der Seite eine Datei überzogen wird, aktiviert sich der Upload-Bereich.
  const [pageDragActive, setPageDragActive] = useState(false);
  const dragDepth = useRef(0);
  // Analyse-Moment (17b): laufende Nummer jedes Parse-Laufs. "Analyse abbrechen" erhoeht sie,
  // damit ein danach eintreffendes Ergebnis den Abbruch nicht ueberschreibt.
  const parseRunRef = useRef(0);
  // Fortschritts-Erzaehlung (17b) — jetzt ECHT: jeder Schritt entspricht einem realen KI-Aufruf
  // (Karte lesen, Rechtschreibung, Allergene). Status pro Schritt + echte Ergebniszahl.
  const [analysisSteps, setAnalysisSteps] = useState<("pending" | "running" | "done" | "failed")[]>(ANALYSIS_STAGES.map(() => "pending"));
  const [analysisCounts, setAnalysisCounts] = useState<(number | null)[]>(ANALYSIS_STAGES.map(() => null));
  function stufe(i: number, st: "pending" | "running" | "done" | "failed", count?: number) {
    setAnalysisSteps((p) => p.map((x, k) => (k === i ? st : x)));
    if (count !== undefined) setAnalysisCounts((c) => c.map((x, k) => (k === i ? count : x)));
  }
  // Cockpit-Retry: KI-Schritt (Allergene/Rechtschreibung) im Review erneut anfordern.
  const [enrichBusy, setEnrichBusy] = useState(false);

  // Nimmt nur PDF/Bild-Dateien innerhalb des Größenlimits an - für Klick UND Drop (behebt die
  // frühere Inkonsistenz: Drop filterte, die Klick-Auswahl nicht). M3-Fix: Größe VOR jeder
  // Base64-Verarbeitung prüfen, damit übergroße Dateien gar nicht erst kodiert/hochgeladen werden.
  function acceptFiles(list: FileList | null) {
    const typed = Array.from(list ?? []).filter((f) => f.type === "application/pdf" || f.type.startsWith("image/"));
    const tooBig = typed.filter((f) => f.size > MAX_FILE_BYTES);
    const ok = typed.filter((f) => f.size <= MAX_FILE_BYTES);
    if (tooBig.length > 0) {
      setError(`Datei zu groß: Das Limit beträgt 8 MB (${tooBig.map((f) => f.name).join(", ")}).`);
      if (ok.length === 0) return; // nichts Gültiges dabei -> abbrechen, keine Base64-Verarbeitung
    } else {
      setError(null);
    }
    if (ok.length > 0) {
      setFiles(ok);
      setSrcTab("file");
      // Neue Datei nach einem Fehlversuch -> zurück in den normalen Grundzustand (9a).
      setParseFailed(false);
      // Neue Datei -> die Namens-Treffer-Nachfrage fuer die vorige Auswahl gilt nicht mehr.
      setNameMatchResolved(false);
      setNameMatchCard(null);
    }
  }

  // Drag-anywhere (Redesign-Wunsch): ein Drag über die GANZE Seite aktiviert den Upload-Bereich
  // und schaltet auf den Datei-Tab. Das window-Drop fängt Ablegen an jeder Stelle ab, damit der
  // Browser die Datei nicht einfach öffnet. Nur im Upload-Schritt aktiv.
  useEffect(() => {
    if (step !== "upload") return;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
    function onEnter(e: DragEvent) {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setPageDragActive(true);
      setSrcTab("file");
    }
    function onOver(e: DragEvent) {
      if (!hasFiles(e)) return;
      e.preventDefault(); // erlaubt Drop und verhindert das Standard-Öffnen der Datei
    }
    function onLeave() {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setPageDragActive(false);
    }
    function onDrop(e: DragEvent) {
      e.preventDefault();
      dragDepth.current = 0;
      setPageDragActive(false);
      if (hasFiles(e)) acceptFiles(e.dataTransfer?.files ?? null);
    }
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
      dragDepth.current = 0;
    };
    // acceptFiles nutzt nur Setter (stabil) — keine veraltete State-Closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  const [job, setJob] = useState<MenuImport | null>(null);
  const [menu, setMenu] = useState<ParsedMenu | null>(null);
  // H-08: nach einem fehlgeschlagenen Parse explizit "Erneut analysieren" (Dateien/Text
  // bleiben erhalten) und "Manuell im Katalog anlegen" anbieten statt einer Sackgasse.
  const [parseFailed, setParseFailed] = useState(false);
  const [allergensConfirmed, setAllergensConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<CommitSummary | null>(null);
  // P1/P2 (docs/KONZEPT-SPEISEKARTEN.md): Ziel-Karte des Imports. Die Karte entsteht erst BEIM
  // Commit (verworfene Imports hinterlassen keine leeren Karten). "replace" (P2) archiviert
  // alles auf der Karte, was der neue Import nicht enthaelt. Deep-Link aus der Karten-
  // Verwaltung: /katalog/import?cardId=<id>&mode=replace waehlt die Karte vor.
  const [searchParams] = useSearchParams();
  const preselectCardId = searchParams.get("cardId");
  const preselectReplace = searchParams.get("mode") === "replace";
  // A3: Deep-Link aus dem Copilot — /katalog/import?importId=<id> lädt genau diesen Import direkt
  // in den Review-Schritt (Rechtschreibung/Allergene), als käme man aus dem Wizard.
  const importId = searchParams.get("importId");
  // Ladeanzeige, solange der deep-verlinkte Import geholt wird (verhindert ein Aufblitzen des
  // Upload-Schritts). Initial true, wenn beim ersten Render bereits eine importId in der URL steht.
  const [deepLinkBusy, setDeepLinkBusy] = useState<boolean>(importId != null);
  const [cardMode, setCardMode] = useState<"create" | "append" | "replace" | "none">(
    preselectCardId ? (preselectReplace ? "replace" : "append") : "create",
  );
  // Befund 31.07.2026 (docs/BACKLOG.md), Auftrag Punkt 1: Namens-Treffer-Erkennung am
  // Haupt-Einstieg. `nameMatchCard` != null -> die Bestaetigungs-Nachfrage steht offen, BEVOR der
  // teure KI-Parse-Lauf startet. `nameMatchResolved` verhindert ein erneutes Nachfragen fuer
  // dieselbe Datei-Auswahl (z. B. beim "Erneut analysieren" nach einem Fehlschlag) - eine neue
  // Datei/Text-Auswahl (acceptFiles) setzt sie zurueck.
  const [nameMatchCard, setNameMatchCard] = useState<MenuCard | null>(null);
  const [nameMatchResolved, setNameMatchResolved] = useState(false);
  const [cardName, setCardName] = useState("");
  const [cardKind, setCardKind] = useState<"food" | "drinks" | "mixed">("food");
  const [appendCardId, setAppendCardId] = useState(preselectCardId ?? "");
  // Mehrsprachigkeit: Sprache der hochgeladenen Karte (Default: erkannte Quellsprache).
  // Wird zur Zielsprache der Uebersetzung, wenn der Import einer Karte zugeordnet wird.
  const [translateLang, setTranslateLang] = useState("en");
  // Verankertes Matching (KONZEPT-MEHRSPRACHIGKEIT, Neufassung): das Modell ordnet die
  // fremdsprachigen Texte den BESTEHENDEN Positionen der Ziel-Karte zu (Anker = Karte),
  // der Mensch bestaetigt jedes Paar. Kein Index-Matching, kein blinder Commit.
  const [pairReview, setPairReview] = useState<{
    cardId: string;
    cardName: string;
    lang: string;
    pairs: TranslationPair[];
    notFound: number;
    totalItems: number;
  } | null>(null);
  const [matchBusy, setMatchBusy] = useState(false);
  // Zuordnungs-Weiche: "pending" = Entscheidung steht aus (Weiche wird VOR dem Review
  // gezeigt, sobald sie relevant ist), "standalone" = bewusst als eigenstaendige Karte
  // weiter. Deep-Links aus der Karten-Verwaltung (ersetzen/ergaenzen) ueberspringen sie.
  const [gateChoice, setGateChoice] = useState<"pending" | "standalone">(preselectCardId ? "standalone" : "pending");
  // Rechtschreib-Vorschlaege (KI prueft, Mensch uebernimmt): beim Parse mit dem ORIGINAL-Text
  // eingefroren. Ein Chip erscheint nur, solange das Feld noch exakt das Original enthaelt -
  // damit zeigen verschobene Indizes (geloeschte Zeilen) oder eigene Edits nie falsche Vorschlaege.
  const [spellSugs, setSpellSugs] = useState<(SpellSuggestion & { original: string })[]>([]);
  // LMIV-Allergen-Vorschlaege (KI schlaegt vor, Mensch bestaetigt): beim Parse eingefroren,
  // nur fuer Positionen, die zu dem Zeitpunkt KEINE Allergene hatten (null). Ein Chip erscheint
  // nur, solange die Position noch ungeklaert (null) ist - sobald der Betreiber Codes setzt
  // oder "keine" bestaetigt, verschwindet der Vorschlag.
  const [allergenSugs, setAllergenSugs] = useState<AllergenSuggestion[]>([]);
  // Befund 31.07.2026, Auftrag Punkt 2: Aehnlichkeits-Warnung ("sehr aehnlich", nicht identisch)
  // zu bereits bestehenden Katalogpositionen - beim Parse EINGEFROREN (guenstige String-Metrik,
  // lib/positions-aehnlichkeit.ts, KEIN weiterer KI-Aufruf), reine Anzeige. Wie bei
  // spellSugs/allergenSugs: der Hinweis verschwindet, sobald die Position editiert wurde.
  const [similaritySugs, setSimilaritySugs] = useState<{ sectionIndex: number; itemIndex: number; original: string; match: { name: string; priceCents: number } }[]>([]);
  // 16c Abschluss-Dialog: offen? + Umgang mit noch offenen Positionen (Default = Entwurf behalten).
  const [finishOpen, setFinishOpen] = useState(false);
  const [draftChoice, setDraftChoice] = useState<"confirm" | "draft" | "clarify">("draft");
  // Nach einem Teil-Commit: Banner „N Positionen als Entwurf behalten".
  const [partialNotice, setPartialNotice] = useState<number | null>(null);
  // 16a Prüf-Cockpit: Filter der Positionsliste + Undo-Toast fuer Sammel-Aktionen.
  const [reviewFilter, setReviewFilter] = useState<"all" | "open" | "suggestions" | "checked">("all");
  const [undoState, setUndoState] = useState<{ snapshot: ParsedMenu; label: string } | null>(null);
  // 16b Fokus-Klären: tastaturgesteuerte Queue, Position fuer Position.
  const [focusOpen, setFocusOpen] = useState(false);
  const [focusTab, setFocusTab] = useState<"all" | "spell" | "allergen">("all");
  const [focusPos, setFocusPos] = useState(0);
  const [focusBaseline, setFocusBaseline] = useState(0); // Anzahl offener Aufgaben beim Start/Tab-Wechsel
  const [focusPick, setFocusPick] = useState<string[] | null>(null); // „Andere wählen": lokale Code-Auswahl
  // Geführter Ablauf: erst Rechtschreibung, dann Allergene (zentrierte Fokus-Karten), dann die
  // volle Karte. "list" = Endkontrolle. Wird nach der Analyse anhand der offenen Punkte gesetzt.
  const [reviewPhase, setReviewPhase] = useState<"spell" | "allergens" | "list">("list");
  const [guidedPos, setGuidedPos] = useState(0);
  const [guidedBaseline, setGuidedBaseline] = useState(0);
  // Rechtschreib-Schritt ist gescheitert (transiente KI-Ueberlast) - NICHT still ueberspringen,
  // sondern die Phase betreten und einen sichtbaren „erneut versuchen"-Knopf zeigen.
  const [spellFailed, setSpellFailed] = useState(false);
  // Kurze neutrale Rueckmeldung nach einem nachtraeglichen KI-Aufruf (z. B. „keine Fehler gefunden").
  const [enrichNotice, setEnrichNotice] = useState<string | null>(null);
  // Undo-Verlauf des gefuehrten Ablaufs: jede bestaetigte/uebersprungene Aktion legt einen
  // Snapshot ab, damit „Zurueck" auch ein bereits abgeschicktes Gericht rueckgaengig macht -
  // ueber Positions- UND Phasengrenzen hinweg (zurueck zum letzten Gericht).
  type GuidedSnap = {
    menu: ParsedMenu;
    spellSugs: (SpellSuggestion & { original: string })[];
    allergenSugs: AllergenSuggestion[];
    guidedPos: number;
    reviewPhase: "spell" | "allergens" | "list";
  };
  const [guidedHistory, setGuidedHistory] = useState<GuidedSnap[]>([]);
  // Weiche (15b): manuell gewaehlte Ziel-Karte, unabhaengig vom appendCardId des Review-Blocks.
  const [gateManualCardId, setGateManualCardId] = useState("");
  const cardsQ = useApi<MenuCard[]>("/api/menu-cards");
  const existingCards = cardsQ.data ?? [];
  // Punkt 150 P1b: Kartenart (Mittags-/Abend-/Wochenkarte …) mit Weiche und KI-Vorschlag.
  const typesQ = useApi<MenuCardType[]>("/api/menu-card-types");
  const cardTypes = typesQ.data ?? [];
  const [cardTypeId, setCardTypeId] = useState<string | null>(null);
  const [kartenartAntwort, setKartenartAntwort] = useState<KartenartAntwort>(null);
  const [aufforderungWeg, setAufforderungWeg] = useState(lesenAufforderungWeg);
  const kiVorschlagFuer = useRef<string | null>(null);
  const kartenDerArt = cardTypeId ? existingCards.filter((k) => k.cardTypeId === cardTypeId) : [];
  function waehleKartenart(id: string | null, typen: MenuCardType[] = cardTypes) {
    setCardTypeId(id);
    setKartenartAntwort(null);
    if (!id) return;
    if (existingCards.filter((k) => k.cardTypeId === id).length === 0) {
      setCardMode("create");
      const name = typen.find((t) => t.id === id)?.name;
      if (name && !cardName.trim()) setCardName(name);
    }
  }
  function beantworteKartenart(a: "ersetzen" | "zusaetzlich") {
    setKartenartAntwort(a);
    if (a === "zusaetzlich") {
      setCardMode("create");
      return;
    }
    setCardMode("replace");
    setAppendCardId(kartenDerArt.length === 1 ? kartenDerArt[0]!.id : kartenDerArt.some((k) => k.id === appendCardId) ? appendCardId : "");
  }
  async function neueKartenart(name: string) {
    try {
      const t = await apiMutate<MenuCardType>("/api/menu-card-types", "POST", { name });
      typesQ.reload();
      waehleKartenart(t.id, [...cardTypes, t]);
    } catch (e) {
      setError(e instanceof Error && e.message.includes("card_type_name_taken") ? `Die Kartenart „${name}“ gibt es schon.` : "Kartenart konnte nicht angelegt werden.");
    }
  }
  // §5.8: Importe mit status='failed' - die Dokumente, die nie im Katalog angekommen sind.
  const fehlgeschlageneQ = useApi<NichtEingelesenesDokument[]>("/api/menu-imports/nicht-eingelesen");
  // Uebersetzungs-Erkennung: Katalog + Kategorien fuer den Preis-Fingerprint je Karte.
  const detectItemsQ = useApi<CatalogItem[]>("/api/catalog/items");
  const detectCatsQ = useApi<CatalogCategory[]>("/api/catalog/categories");
  // 150 P2 (K5): Preissprung > 30 % ggue. der ersetzten Karte — nur diese Admin-Route kennt den Altpreis.
  const spruengeQ = useApi<{ sectionIndex: number; itemIndex: number; altCents: number }[]>(
    step === "review" && job && cardMode === "replace" && appendCardId ? `/api/menu-imports/${job.id}/preisspruenge?cardId=${appendCardId}` : null,
  );
  /** Altpreis der ersetzten Karte fuer diese Position, solange sie lokal noch dieselbe ist (Name wie beim Lesen). */
  function altpreisFuer(si: number, ii: number): number | null {
    const treffer = spruengeQ.data?.find((x) => x.sectionIndex === si && x.itemIndex === ii);
    if (!treffer) return null;
    const gelesen = job?.parsedPayload?.sections[si]?.items[ii]?.name;
    return gelesen != null && gelesen === menu?.sections[si]?.items[ii]?.name ? treffer.altCents : null;
  }
  // Vorschlag einmal abgelehnt/uebernommen -> nicht erneut aufdraengen.
  const [detectionDismissed, setDetectionDismissed] = useState(false);

  /**
   * Uebersetzungs-Erkennung ueber den PREIS-FINGERABDRUCK: Sprachversionen derselben Karte
   * haben (fast) identische Preislisten - sprachunabhaengig und robust gegen Layout-Nuancen.
   * Vergleicht die Preis-Multimenge der geparsten Karte mit jeder bestehenden Karte
   * (Overlap / groessere Menge). Nur ein VORSCHLAG - der Betreiber entscheidet.
   */
  const translationCandidate = useMemo((): { card: MenuCard; score: number; overlap: number } | null => {
    if (!menu || existingCards.length === 0) return null;
    const parsedPrices: number[] = [];
    for (const s of menu.sections) for (const it of s.items) if (it.priceCents != null && it.priceCents > 0) parsedPrices.push(it.priceCents);
    if (parsedPrices.length < 5) return null; // zu wenig Signal
    const cardByCategory = new Map((detectCatsQ.data ?? []).map((c) => [c.id, c.cardId ?? null]));
    const pricesByCard = new Map<string, number[]>();
    for (const it of detectItemsQ.data ?? []) {
      if (it.archivedAt != null || it.priceCents <= 0 || !it.categoryId) continue;
      const cardId = cardByCategory.get(it.categoryId);
      if (!cardId) continue;
      pricesByCard.set(cardId, [...(pricesByCard.get(cardId) ?? []), it.priceCents]);
    }
    const toMultiset = (arr: number[]) => {
      const m = new Map<number, number>();
      for (const p of arr) m.set(p, (m.get(p) ?? 0) + 1);
      return m;
    };
    const parsedSet = toMultiset(parsedPrices);
    let best: { card: MenuCard; score: number; overlap: number } | null = null;
    for (const card of existingCards) {
      const cardPrices = pricesByCard.get(card.id) ?? [];
      if (cardPrices.length < 5) continue;
      const cardSet = toMultiset(cardPrices);
      let overlap = 0;
      for (const [price, n] of parsedSet) overlap += Math.min(n, cardSet.get(price) ?? 0);
      const score = overlap / Math.max(parsedPrices.length, cardPrices.length);
      if (score > (best?.score ?? 0)) best = { card, score, overlap };
    }
    // Schwelle: >=80 % Preisuebereinstimmung; bei fremdsprachiger Quelle reicht das klar,
    // bei gleicher Sprache waere es eher ein Duplikat -> trotzdem anzeigen (Betreiber entscheidet).
    return best && best.score >= 0.8 ? best : null;
  }, [menu, existingCards, detectItemsQ.data, detectCatsQ.data]);

  const flags: SanityFlag[] = job?.confidence?.flags ?? [];
  const kiKartenart = job?.confidence?.cardTypeSuggestion ?? null;
  useEffect(() => {
    if (!job || !kiKartenart || preselectCardId || cardsQ.data == null || kiVorschlagFuer.current === job.id) return;
    kiVorschlagFuer.current = job.id;
    if (cardTypeId === null) waehleKartenart(kiKartenart.cardTypeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, kiKartenart?.cardTypeId, cardsQ.data]);
  const flagFor = useMemo(() => {
    const map = new Map<string, SanityFlag[]>();
    for (const f of flags) {
      const key = `${f.sectionIndex}:${f.itemIndex ?? "s"}`;
      map.set(key, [...(map.get(key) ?? []), f]);
    }
    return map;
  }, [flags]);

  /** Rechtschreib-Vorschlaege mit Original-Text einfrieren (Chip erscheint nur bei unveraendertem Feld). */
  function freezeSpell(parsedMenu: ParsedMenu, corrections: SpellSuggestion[]) {
    const sugs: (SpellSuggestion & { original: string })[] = [];
    for (const sug of corrections) {
      const sec = parsedMenu.sections[sug.sectionIndex];
      if (!sec) continue;
      if (sug.itemIndex === null) {
        if (sug.field === "title" && sec.title !== sug.corrected) sugs.push({ ...sug, original: sec.title });
      } else {
        const it = sec.items[sug.itemIndex];
        if (!it) continue;
        const original = sug.field === "name" ? it.name : (it.description ?? "");
        if (original !== sug.corrected) sugs.push({ ...sug, original });
      }
    }
    setSpellSugs(sugs);
  }
  /** Allergen-Vorschlaege einfrieren: nur fuer aktuell ungeklaerte (null) Positionen. */
  function freezeAllergen(parsedMenu: ParsedMenu, suggestions: AllergenSuggestion[]) {
    setAllergenSugs(suggestions.filter((s) => parsedMenu.sections[s.sectionIndex]?.items[s.itemIndex]?.allergens === null && s.codes.length > 0));
  }
  /** Aehnlichkeits-Warnung einfrieren (Auftrag Punkt 2): pro neu erkannter Position prueft eine
   *  guenstige String-Metrik (kein KI-Aufruf) gegen die NICHT archivierten Katalogpositionen. Nur
   *  ein Hinweis - fuehrt nichts zusammen, aendert keine Daten. */
  function freezeSimilarity(parsedMenu: ParsedMenu, katalog: readonly CatalogItem[]) {
    const aktivKatalog = katalog.filter((it) => it.archivedAt == null);
    const sugs: typeof similaritySugs = [];
    parsedMenu.sections.forEach((sec, si) => {
      sec.items.forEach((it, ii) => {
        const treffer = findeAehnlichePosition(it.name, aktivKatalog);
        if (treffer) sugs.push({ sectionIndex: si, itemIndex: ii, original: it.name, match: { name: treffer.name, priceCents: treffer.priceCents } });
      });
    });
    setSimilaritySugs(sugs);
  }
  /** Aehnlichkeits-Hinweis fuer EINE Position, nur solange sie noch den eingefrorenen Original-
   *  Namen traegt (wie bei Rechtschreib-/Allergen-Vorschlaegen - ein Edit macht den Hinweis
   *  hinfaellig, es wird NICHT live neu gerechnet). */
  function similarSuggestionFor(si: number, ii: number) {
    const sug = similaritySugs.find((s) => s.sectionIndex === si && s.itemIndex === ii);
    if (!sug) return null;
    if (menu?.sections[si]?.items[ii]?.name !== sug.original) return null;
    return sug;
  }
  /** Ein Anreicherungs-Schritt (/enrich) — eigener KI-Aufruf mit Retry serverseitig. */
  async function enrichStep(jobId: string, stepKind: "spell" | "allergens"): Promise<{ count: number; corrections?: SpellSuggestion[]; suggestions?: AllergenSuggestion[] }> {
    const res = await apiFetch(`/api/menu-imports/${jobId}/enrich`, { method: "POST", body: JSON.stringify({ step: stepKind }) });
    if (!res.ok) throw await toApiError(res);
    return ((await res.json()) as { data: { count: number; corrections?: SpellSuggestion[]; suggestions?: AllergenSuggestion[] } }).data;
  }
  /** 150 P2: Uebersetzungs-Schritt einer Sprache; uebernimmt die Vorschlaege in den lokalen Stand. */
  async function translateStep(jobId: string, lang: ImportSprache): Promise<{ count: number }> {
    const res = await apiFetch(`/api/menu-imports/${jobId}/enrich`, { method: "POST", body: JSON.stringify({ step: "translate", lang }) });
    if (!res.ok) throw await toApiError(res);
    const data = ((await res.json()) as { data: { count: number; parsedPayload?: ParsedMenu | null } }).data;
    const server = data.parsedPayload;
    if (server) setMenu((prev) => (prev ? uebernehmeUebersetzungen(prev, server) : prev));
    return { count: data.count };
  }
  /** Nachtraeglich (aus dem Cockpit) eine Sprache erneut uebersetzen lassen. */
  async function requestTranslation(lang: ImportSprache) {
    if (!job) return;
    setEnrichBusy(true);
    setError(null);
    setEnrichNotice(null);
    try {
      const t = await translateStep(job.id, lang);
      setEnrichNotice(`${SPRACH_KURZ[lang]}: ${t.count} Texte übersetzt.`);
    } catch (e) {
      setError(importError(e instanceof Error ? e.message : String(e)));
    } finally {
      setEnrichBusy(false);
    }
  }
  /** Nachtraeglich (aus dem Cockpit) KI-Allergen-Vorschlaege erneut anfordern. */
  async function requestAllergenSuggestions() {
    if (!job || !menu) return;
    setEnrichBusy(true);
    setError(null);
    setEnrichNotice(null);
    try {
      const a = await enrichStep(job.id, "allergens");
      freezeAllergen(menu, a.suggestions ?? []);
      const unresolved = menu.sections.some((s) => s.items.some((it) => it.allergens === null));
      if (unresolved) { setGuidedHistory([]); setGuidedPos(0); setReviewPhase("allergens"); }
      else setEnrichNotice("Alle Allergene sind bereits geklärt.");
    } catch (e) {
      setError(importError(e instanceof Error ? e.message : String(e)));
    } finally {
      setEnrichBusy(false);
    }
  }

  /** Nachtraeglich (aus dem Cockpit) die Rechtschreibpruefung erneut aufrufen. */
  async function requestSpellCheck() {
    if (!job || !menu) return;
    setEnrichBusy(true);
    setError(null);
    setEnrichNotice(null);
    try {
      const s = await enrichStep(job.id, "spell");
      freezeSpell(menu, s.corrections ?? []);
      setSpellFailed(false);
      if (s.count > 0) { setGuidedHistory([]); setGuidedPos(0); setReviewPhase("spell"); }
      else setEnrichNotice("Keine Rechtschreibfehler gefunden.");
    } catch (e) {
      setSpellFailed(true);
      setError(importError(e instanceof Error ? e.message : String(e)));
    } finally {
      setEnrichBusy(false);
    }
  }

  /**
   * Namens-Treffer-Erkennung (Befund 31.07.2026, Auftrag Punkt 1): rein clientseitiger
   * Vergleich VOR dem teuren KI-Parse-Lauf, s. Begruendung in lib/karten-namenstreffer.ts.
   * Nur relevant am Haupt-Einstieg (kein `preselectCardId` - Deep-Links haben ihre Entscheidung
   * schon getroffen) und nur, solange `cardMode` noch beim Default "create" steht: hat der
   * Betreiber die Nachfrage bereits einmal beantwortet (Ersetzen ODER "als neu anlegen"), gilt
   * das fuer diese Datei-Auswahl weiter - kein erneutes Nachfragen bei "Erneut analysieren".
   */
  function findeNameMatch(): MenuCard | null {
    if (preselectCardId || cardMode !== "create" || nameMatchResolved) return null;
    const kandidat = files[0] ? kartennameAusDateiname(files[0].name) : null;
    return findeKartenNamenstreffer(kandidat, existingCards);
  }

  /** Klick auf "Karte analysieren"/"Erneut analysieren": erst der Namens-Treffer-Check, dann
   *  (wenn kein Treffer oder bereits beantwortet) der eigentliche KI-Parse-Lauf. */
  function handleAnalyzeClick() {
    const treffer = findeNameMatch();
    if (treffer) {
      setNameMatchCard(treffer);
      return;
    }
    void startParse();
  }

  /** Beantwortet die Namens-Treffer-Nachfrage: "ersetzen" setzt cardMode/appendCardId auf die
   *  gefundene Karte (die Vorgaengerkarte wird beim Commit archiviert - der bestehende
   *  `replace`-Pfad, jetzt nur ueber den Regelweg erreicht), "neu" laesst `create` unveraendert.
   *  Startet in JEDEM Fall danach den Parse-Lauf - nie stillschweigend. */
  function resolveNameMatch(choice: "replace" | "create") {
    if (choice === "replace" && nameMatchCard) {
      setCardMode("replace");
      setAppendCardId(nameMatchCard.id);
    }
    setNameMatchResolved(true);
    setNameMatchCard(null);
    void startParse();
  }

  async function startParse() {
    setError(null);
    setParseFailed(false);
    if (files.length === 0 && text.trim() === "") {
      setError("Bitte eine PDF/Foto hochladen oder Text einfügen.");
      return;
    }
    const myRun = ++parseRunRef.current; // Abbruch-Erkennung (17b)
    setPartialNotice(null);
    setUndoState(null);
    setReviewFilter("all");
    setFocusOpen(false);
    setSpellSugs([]);
    setAllergenSugs([]);
    setSimilaritySugs([]);
    setSpellFailed(false);
    setEnrichNotice(null);
    setGuidedHistory([]);
    setAnalysisSteps(ANALYSIS_STAGES.map((_, i) => (i === 0 ? "running" : "pending")));
    setAnalysisCounts(ANALYSIS_STAGES.map(() => null));
    setStep("parsing");
    try {
      const encoded = await Promise.all(files.map(fileToBase64));
      const sourceKind = files.length > 0 ? (files[0]!.type === "application/pdf" ? "pdf" : "image") : "text";
      const res = await apiFetch("/api/menu-imports", {
        method: "POST",
        body: JSON.stringify({
          sourceKind,
          sourceLang: "de",
          files: encoded.length ? encoded : undefined,
          textContent: text.trim() || undefined,
        }),
      });
      const body = (await res.json().catch(() => null)) as { data?: MenuImport; message?: string; error?: string } | null;
      if (parseRunRef.current !== myRun) return; // waehrend der Analyse abgebrochen -> Ergebnis verwerfen
      if (!res.ok || !body?.data) {
        // Reale Server-Meldung anzeigen (nicht erfinden).
        setError(importError(body?.message, body?.error));
        setParseFailed(true);
        setStep("upload");
        return;
      }
      setJob(body.data);
      const parsedMenu = body.data.parsedPayload;
      setMenu(parsedMenu);
      setDetectionDismissed(false);
      // Auftrag Punkt 2: Aehnlichkeits-Warnung einmalig einfrieren (guenstige String-Metrik,
      // kein KI-Aufruf) - `detectItemsQ` laeuft seit dem Mount und ist bei den 30-100s des
      // KI-Parse-Laufs so gut wie immer laengst geladen.
      if (parsedMenu) freezeSimilarity(parsedMenu, detectItemsQ.data ?? []);
      // Schritt 1 „Karte lesen" ist echt fertig — mit echter Positionszahl.
      const itemN = parsedMenu?.sections.reduce((n, s) => n + s.items.length, 0) ?? 0;
      stufe(0, "done", itemN);
      stufe(1, "running");
      // Mehrsprachigkeit: erkannte Quellsprache als Uebersetzungs-Zielsprache vorbelegen.
      if (body.data.sourceLang && /^[a-z]{2}$/.test(body.data.sourceLang) && body.data.sourceLang !== "de") {
        setTranslateLang(body.data.sourceLang);
      }
      // P1: Karten-Namen aus dem Dateinamen vorbelegen (nur wenn noch nichts eingetippt).
      setCardName((prev) => {
        if (prev.trim()) return prev;
        const base = files[0]?.name.replace(/\.[a-z0-9]+$/i, "").trim();
        return (base || `Karte ${new Date().toLocaleDateString("de-DE")}`).slice(0, 120);
      });

      // Schritt 2: Rechtschreibung (echter KI-Aufruf). Fehler = sichtbar, kippt den Import nicht.
      let spellHad = 0;
      let spellDidFail = false;
      if (parsedMenu) {
        try {
          const s = await enrichStep(body.data.id, "spell");
          if (parseRunRef.current !== myRun) return;
          freezeSpell(parsedMenu, s.corrections ?? []);
          spellHad = s.count;
          stufe(1, "done", s.count);
        } catch {
          if (parseRunRef.current !== myRun) return;
          // NICHT still ueberspringen: Fehlschlag merken, damit die Rechtschreib-Phase
          // trotzdem betreten und ein „erneut versuchen" angeboten wird (statt direkt Allergene).
          spellDidFail = true;
          setSpellFailed(true);
          stufe(1, "failed");
        }
        stufe(2, "running");
        // Schritt 3: Allergene (echter KI-Aufruf).
        try {
          const a = await enrichStep(body.data.id, "allergens");
          if (parseRunRef.current !== myRun) return;
          freezeAllergen(parsedMenu, a.suggestions ?? []);
          stufe(2, "done", a.count);
        } catch {
          if (parseRunRef.current !== myRun) return;
          stufe(2, "failed");
        }
        // Schritte 4-6 (150 P2): Übersetzung EN/IT/FR, ohne Zwischenklick. Fehler kippt nichts,
        // die Sprache bleibt im Cockpit einzeln wiederholbar.
        for (const [k, lang] of IMPORT_SPRACHEN.entries()) {
          stufe(ERSTE_UEBERSETZUNGS_STUFE + k, "running");
          try {
            const t = await translateStep(body.data.id, lang);
            if (parseRunRef.current !== myRun) return;
            stufe(ERSTE_UEBERSETZUNGS_STUFE + k, "done", t.count);
          } catch {
            if (parseRunRef.current !== myRun) return;
            stufe(ERSTE_UEBERSETZUNGS_STUFE + k, "failed");
          }
        }
      }
      if (parseRunRef.current !== myRun) return;
      // Geführter Ablauf: mit der ersten offenen Phase starten (Rechtschreibung -> Allergene -> Karte).
      // Bei gescheiterter Rechtschreibpruefung die Phase TROTZDEM betreten (Retry sichtbar).
      const allergenOpen = parsedMenu?.sections.some((s) => s.items.some((it) => it.allergens === null)) ?? false;
      setGuidedPos(0);
      setReviewPhase(spellHad > 0 || spellDidFail ? "spell" : allergenOpen ? "allergens" : "list");
      setStep("review");
    } catch (e) {
      if (parseRunRef.current !== myRun) return; // Abbruch: keinen Fehler des verworfenen Laufs zeigen
      setError(e instanceof Error ? e.message : String(e));
      setParseFailed(true);
      setStep("upload");
    }
  }

  // A3: Deep-verlinkten Import laden und direkt in den Review-Schritt setzen — mit denselben
  // eingefrorenen KI-Vorschlägen (confidence.spell / confidence.allergenSuggestions), als käme man
  // aus dem Wizard. Ein Ref merkt sich die geladene Id, damit Re-Renders/State-Updates den Review
  // nicht überschreiben; ändert der Copilot die importId (neue Navigation), wird frisch geladen.
  // E2E-Fund (speisekarten-import.spec.ts, 17.08.2026): `loadedImportRef.current` wurde HIER,
  // synchron VOR dem eigentlichen Fetch, gesetzt. Unter React StrictMode (main.tsx, nur Dev)
  // laeuft dieser Effect beim Mount zweimal (Mount -> Cleanup -> Mount) - Lauf 1 setzt die Ref
  // sofort, wird dann durch die simulierte Cleanup abgebrochen (`alive=false`), sein Fetch
  // beendet sich spaeter still ueber `if (!alive) return`. Lauf 2 sieht `loadedImportRef.current
  // === id` bereits erfuellt und tut GAR NICHTS - `deepLinkBusy` bleibt fuer immer `true`
  // (reproduzierbar: GET /api/menu-imports/:id kam serverseitig 200 zurueck, die UI blieb trotzdem
  // dauerhaft auf "Import wird geladen …" stehen). Fix: die Ref erst bei ECHTEM Erfolg/Fehlschlag
  // setzen (unten), nicht vorab als Lock - ein doppelter GET unter StrictMode ist harmlos (wie bei
  // jedem anderen `useApi`-Hook auf dieser Seite), ein staendig haengender Spinner nicht.
  const loadedImportRef = useRef<string | null>(null);
  useEffect(() => {
    const id = importId;
    if (!id || loadedImportRef.current === id) return;
    let alive = true;
    setDeepLinkBusy(true);
    setError(null);
    (async () => {
      try {
        const loaded = await apiMutate<MenuImport>(`/api/menu-imports/${id}`, "GET");
        if (!alive) return;
        const parsedMenu = loaded.parsedPayload;
        if (loaded.status !== "review" || !parsedMenu) {
          // Kein gültiger Review-Zustand -> ehrliche Meldung, zurück in den Upload-Grundzustand.
          // Ref NICHT setzen (siehe Kommentar am Effect-Anfang) - ein erneuter Versuch mit
          // derselben id bleibt moeglich (z.B. wenn der Job zwischenzeitlich fertig geparst wird).
          setError(importError(loaded.error, `Status: ${loaded.status}`));
          setStep("upload");
          setDeepLinkBusy(false);
          return;
        }
        // Transienten Review-State zurücksetzen (wie nach einem frischen Parse).
        setParseFailed(false);
        setPartialNotice(null);
        setUndoState(null);
        setReviewFilter("all");
        setFocusOpen(false);
        setSpellFailed(false);
        setEnrichNotice(null);
        setGuidedHistory([]);
        setAllergensConfirmed(false);
        setJob(loaded);
        setMenu(parsedMenu);
        setDetectionDismissed(false);
        // Eingefrorene KI-Vorschläge aus der gespeicherten confidence übernehmen. Die Chips
        // erscheinen ohnehin nur, solange Feld/Position noch unverändert bzw. ungeklärt sind.
        const corrections = loaded.confidence?.spell ?? [];
        const suggestions = loaded.confidence?.allergenSuggestions ?? [];
        freezeSpell(parsedMenu, corrections);
        freezeAllergen(parsedMenu, suggestions);
        // Aehnlichkeits-Warnung wird NICHT persistiert (reiner Client-Hinweis, kein KI-Aufruf) -
        // beim Deep-Link-Wiedereinstieg frisch gegen den aktuellen Katalog-Stand berechnen.
        freezeSimilarity(parsedMenu, detectItemsQ.data ?? []);
        // Mehrsprachigkeit: erkannte Quellsprache als Übersetzungs-Zielsprache vorbelegen.
        if (loaded.sourceLang && /^[a-z]{2}$/.test(loaded.sourceLang) && loaded.sourceLang !== "de") {
          setTranslateLang(loaded.sourceLang);
        }
        // Erste offene Phase wie im Wizard: Rechtschreibung -> Allergene -> Endkontrolle.
        const allergenOpen = parsedMenu.sections.some((s) => s.items.some((it) => it.allergens === null));
        setGuidedPos(0);
        setReviewPhase(corrections.length > 0 ? "spell" : allergenOpen ? "allergens" : "list");
        setStep("review");
        setDeepLinkBusy(false);
        // Erst JETZT, bei echtem Erfolg, als geladen markieren (siehe Kommentar am Effect-Anfang).
        loadedImportRef.current = id;
      } catch (e) {
        if (!alive) return;
        // Ref bleibt unbesetzt - erneuter Versuch mit derselben id bleibt möglich.
        setError(importError(e instanceof Error ? e.message : String(e)));
        setStep("upload");
        setDeepLinkBusy(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importId]);

  function patchItem(si: number, ii: number, patch: Partial<ParsedMenu["sections"][0]["items"][0]>) {
    // L6-Fix: funktionaler Updater statt Closure-Snapshot. Mehrere schnelle Folge-Updates
    // (z. B. Allergen-Bestaetigungen kurz hintereinander) gingen sonst verloren, weil jeder
    // Aufruf vom selben `menu`-Snapshot ausging und das letzte setMenu die vorigen ueberschrieb.
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      next.sections[si]!.items[ii] = { ...next.sections[si]!.items[ii]!, ...patch };
      return next;
    });
  }
  function patchSection(si: number, patch: Partial<ParsedMenu["sections"][0]>) {
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      next.sections[si] = { ...next.sections[si]!, ...patch };
      return next;
    });
  }
  function patchVariant(si: number, vi: number, patch: Partial<{ label: string; priceCents: number }>) {
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      const variants = [...(next.sections[si]!.menuVariants ?? [])];
      variants[vi] = { ...variants[vi]!, ...patch };
      next.sections[si] = { ...next.sections[si]!, menuVariants: variants };
      return next;
    });
  }
  function addVariant(si: number) {
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      next.sections[si] = {
        ...next.sections[si]!,
        menuVariants: [...(next.sections[si]!.menuVariants ?? []), { label: "mit Weinbegleitung", priceCents: 0 }],
      };
      return next;
    });
  }
  function removeVariant(si: number, vi: number) {
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      const variants = [...(next.sections[si]!.menuVariants ?? [])];
      variants.splice(vi, 1);
      next.sections[si] = { ...next.sections[si]!, menuVariants: variants };
      return next;
    });
  }
  function removeItem(si: number, ii: number) {
    // L6-Fix: funktionaler Updater (siehe patchItem) — race-sicher gegen schnelle Folge-Updates.
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      next.sections[si]!.items.splice(ii, 1);
      return next;
    });
  }

  // Sammel-Bestaetigung: alle Positionen mit ungeklaerten Allergenen (null) in EINEM
  // Update auf "geprueft, keine LMIV-Hauptallergene" ([]) setzen. Spart bei grossen Karten
  // das Einzel-Bestaetigen. Bereits gesetzte Positionen bleiben unveraendert.
  function confirmAllUnresolvedAllergens() {
    setMenu((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      for (const s of next.sections) {
        for (const it of s.items) {
          if (it.allergens === null) it.allergens = [];
        }
      }
      return next;
    });
  }

  /** Offene Rechtschreib-Vorschlaege: Feld enthaelt noch exakt den Original-Text. */
  function openSpellSuggestions(): (SpellSuggestion & { original: string })[] {
    if (!menu) return [];
    return spellSugs.filter((sug) => {
      const sec = menu.sections[sug.sectionIndex];
      if (!sec) return false;
      if (sug.itemIndex === null) return sec.title === sug.original;
      const it = sec.items[sug.itemIndex];
      if (!it) return false;
      return (sug.field === "name" ? it.name : (it.description ?? "")) === sug.original;
    });
  }
  function applySpell(sug: SpellSuggestion & { original: string }) {
    if (sug.itemIndex === null) patchSection(sug.sectionIndex, { title: sug.corrected });
    else if (sug.field === "name") patchItem(sug.sectionIndex, sug.itemIndex, { name: sug.corrected });
    else patchItem(sug.sectionIndex, sug.itemIndex, { description: sug.corrected });
  }
  function applyAllSpell() {
    for (const sug of openSpellSuggestions()) applySpell(sug);
  }

  /** Offener Allergen-Vorschlag fuer genau diese Position (nur solange sie ungeklaert/null ist). */
  function allergenSuggestionFor(si: number, ii: number): AllergenSuggestion | null {
    if (!menu) return null;
    const it = menu.sections[si]?.items[ii];
    if (!it || it.allergens !== null) return null;
    return allergenSugs.find((s) => s.sectionIndex === si && s.itemIndex === ii) ?? null;
  }
  /** Uebernimmt die vorgeschlagenen Codes als Allergene der Position (Mensch bestaetigt). */
  function applyAllergen(sug: AllergenSuggestion) {
    patchItem(sug.sectionIndex, sug.itemIndex, { allergens: [...sug.codes] });
  }
  /** Offene Allergen-Vorschlaege: Position ist noch ungeklaert (null). */
  function openAllergenSuggestions(): AllergenSuggestion[] {
    if (!menu) return [];
    return allergenSugs.filter((s) => menu.sections[s.sectionIndex]?.items[s.itemIndex]?.allergens === null);
  }
  function applyAllAllergen() {
    for (const sug of openAllergenSuggestions()) applyAllergen(sug);
  }
  /** EIN Klick uebernimmt ALLE offenen KI-Vorschlaege (Rechtschreibung + Allergene) + Undo. */
  function applyAllSuggestions() {
    if (!menu) return;
    const snapshot = structuredClone(menu);
    const n = openSpellSuggestions().length + openAllergenSuggestions().length;
    applyAllSpell();
    applyAllAllergen();
    if (n > 0) setUndoState({ snapshot, label: `${n} KI-Vorschlag${n === 1 ? "" : "äge"} übernommen` });
  }
  function undoLast() {
    if (!undoState) return;
    setMenu(undoState.snapshot);
    setUndoState(null);
  }

  // 16a Prüf-Cockpit: Positions-Status fuer Filter + Fortschritt.
  const openSpellKeys = new Set(openSpellSuggestions().filter((s) => s.itemIndex !== null).map((s) => `${s.sectionIndex}:${s.itemIndex}`));
  const openAllergenKeys = new Set(openAllergenSuggestions().map((s) => `${s.sectionIndex}:${s.itemIndex}`));
  function itemHasSuggestion(si: number, ii: number) {
    return openSpellKeys.has(`${si}:${ii}`) || openAllergenKeys.has(`${si}:${ii}`);
  }
  /** „Geprüft" = Allergene geklärt (nicht null) UND kein offener KI-Vorschlag mehr. */
  function itemIsChecked(si: number, ii: number) {
    const it = menu?.sections[si]?.items[ii];
    return !!it && it.allergens !== null && !itemHasSuggestion(si, ii);
  }
  function itemMatchesFilter(si: number, ii: number) {
    if (reviewFilter === "all") return true;
    if (reviewFilter === "checked") return itemIsChecked(si, ii);
    if (reviewFilter === "suggestions") return itemHasSuggestion(si, ii);
    return !itemIsChecked(si, ii); // "open"
  }
  const cockpit = (() => {
    let total = 0;
    let checked = 0;
    let withSug = 0;
    menu?.sections.forEach((s, si) =>
      s.items.forEach((_, ii) => {
        total++;
        if (itemIsChecked(si, ii)) checked++;
        if (itemHasSuggestion(si, ii)) withSug++;
      }),
    );
    return { total, checked, open: total - checked, withSug };
  })();

  // 16b Fokus-Klären: Aufgaben-Queue (Rechtschreibung + Allergene), Position fuer Position.
  type FocusTask = { kind: "spell"; sug: SpellSuggestion & { original: string } } | { kind: "allergen"; si: number; ii: number };
  const focusSpellCount = openSpellSuggestions().length;
  const focusAllergenCount = menu?.sections.reduce((n, s) => n + s.items.filter((i) => i.allergens === null).length, 0) ?? 0;
  const focusQueue: FocusTask[] = (() => {
    const spellTasks: FocusTask[] = openSpellSuggestions().map((sug) => ({ kind: "spell", sug }));
    const allergenTasks: FocusTask[] = [];
    menu?.sections.forEach((s, si) => s.items.forEach((it, ii) => { if (it.allergens === null) allergenTasks.push({ kind: "allergen", si, ii }); }));
    if (focusTab === "spell") return spellTasks;
    if (focusTab === "allergen") return allergenTasks;
    return [...spellTasks, ...allergenTasks];
  })();
  function openFocus(tab: "all" | "spell" | "allergen" = "all") {
    setFocusTab(tab);
    setFocusPos(0);
    setFocusPick(null);
    setFocusOpen(true);
  }
  function dismissSpell(sug: SpellSuggestion) {
    setSpellSugs((prev) => prev.filter((s) => !(s.sectionIndex === sug.sectionIndex && s.itemIndex === sug.itemIndex && s.field === sug.field)));
  }
  function focusSkip() { setFocusPick(null); setFocusPos((p) => p + 1); }
  function focusSpellApply(t: FocusTask) { if (t.kind === "spell") { snapshotGuided(); applySpell(t.sug); setFocusPick(null); } }
  function focusSpellIgnore(t: FocusTask) { if (t.kind === "spell") { snapshotGuided(); dismissSpell(t.sug); setFocusPick(null); } }
  function focusAllergenNone(t: FocusTask) { if (t.kind === "allergen") { snapshotGuided(); patchItem(t.si, t.ii, { allergens: [] }); setFocusPick(null); } }
  /** Aktuelle Allergen-Auswahl der Karte: getippte Auswahl (focusPick) ODER der KI-Vorschlag. */
  function allergenSelected(t: FocusTask): string[] {
    if (t.kind !== "allergen") return [];
    return focusPick ?? allergenSuggestionFor(t.si, t.ii)?.codes ?? [];
  }
  /** Bestätigt die aktuelle Auswahl (mindestens 1 Code). „Keine Allergene" ist ein eigener Weg. */
  function focusAllergenConfirm(t: FocusTask) {
    if (t.kind !== "allergen") return;
    const codes = allergenSelected(t);
    if (codes.length === 0) return;
    snapshotGuided();
    patchItem(t.si, t.ii, { allergens: [...codes] });
    setFocusPick(null);
  }
  function toggleFocusPick(t: FocusTask, code: string) {
    const cur = allergenSelected(t);
    setFocusPick(cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]);
  }

  /** Karten-Inhalt einer Fokus-Aufgabe (Rechtschreibung ODER Allergene) — geteilt zwischen
   *  Modal (16b) und geführtem Ablauf. */
  function focusTaskBody(cur: FocusTask) {
    if (!menu) return null;
    // 18a Inline-Wortdiff: Satz EINMAL, nur die geänderte Stelle markiert.
    if (cur.kind === "spell") {
      const d = wordDiff(cur.sug.original, cur.sug.corrected);
      // 18b: konkretes Aenderungs-Label, damit der Betreiber nicht selbst nach dem Diff suchen muss.
      // Kleine Aenderungen zeigen ihr Label ("Komma entfernt"); echte Tippfehler zeigen das Wortpaar.
      const change = describeSpellChange(cur.sug.original, cur.sug.corrected);
      const changeLabel =
        change.kind === "big" && d.removed.length > 0 && d.added.length > 0
          ? `Tippfehler: ${d.removed.join(" ")} → ${d.added.join(" ")}`
          : change.label;
      return (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-label-md text-[11px] font-semibold uppercase tracking-wide text-on-surface-variant/70">
              {menu.sections[cur.sug.sectionIndex]?.title} · Rechtschreibung
            </p>
            <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-secondary/10 px-2 py-0.5 font-label-md text-[11px] font-semibold text-secondary dark:text-[#b8ae9e]">
              <Icon name="edit" className="shrink-0 text-[13px]" /> <span className="truncate">{changeLabel} · 1 Änderung</span>
            </span>
          </div>
          {/* Der Satz einmal, geänderte Stelle inline markiert. */}
          <p className="mt-3 rounded-xl border border-outline-variant/40 bg-surface-container-low p-4 font-body-md text-body-md leading-relaxed text-on-background dark:border-outline/30 dark:bg-surface-variant/10 dark:text-inverse-on-surface">
            {d.prefix.join(" ")}{d.prefix.length ? " " : ""}
            {d.removed.map((w, k) => (
              <span key={`r${k}`} className="text-on-surface-variant/60 line-through decoration-red-500/60">{w}{k < d.removed.length - 1 ? " " : ""}</span>
            ))}
            {d.removed.length > 0 && d.added.length > 0 ? " " : ""}
            {d.added.map((w, k) => (
              <span key={`a${k}`} className="rounded bg-primary/10 px-0.5 font-semibold text-primary">{w}{k < d.added.length - 1 ? " " : ""}</span>
            ))}
            {d.suffix.length ? " " : ""}{d.suffix.join(" ")}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3 font-body-sm text-[11px] text-on-surface-variant/70">
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-on-surface-variant/40" /> Entfernt</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-primary" /> Neu</span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={() => focusSpellApply(cur)}><Icon name="check" className="text-[17px]" /> Korrektur übernehmen <kbd className="ml-1 rounded bg-white/20 px-1 text-[11px]">↵</kbd></Button>
            <Button variant="secondary" onClick={() => focusSpellIgnore(cur)}>Original behalten</Button>
          </div>
        </div>
      );
    }
    // 19a/19b: volles LMIV-Raster mit vorausgewählten KI-Vorschlägen + Klartext-Begründung.
    const item = menu.sections[cur.si]?.items[cur.ii];
    const sug = allergenSuggestionFor(cur.si, cur.ii);
    if (!item) return null;
    const selected = allergenSelected(cur);
    const sugCodes = sug?.codes ?? [];
    return (
      <div>
        <p className="font-label-md text-[11px] font-semibold uppercase tracking-wide text-on-surface-variant/70">
          {menu.sections[cur.si]?.title} · Allergene
        </p>
        <p className="mt-2 font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{item.name}</p>
        {item.description && <p className="mt-0.5 font-body-sm text-body-sm text-on-surface-variant/80">{item.description}</p>}

        <p className="mt-4 font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Welche Allergene enthält diese Position?</p>
        {sug ? (
          <p className="mt-1 font-body-sm text-[12px] text-on-surface-variant/80">
            <span className="font-semibold text-secondary dark:text-[#b8ae9e]">✨ Vorschlag vorausgewählt.</span> Antippen zum Anpassen, dann bestätigen.
          </p>
        ) : (
          <p className="mt-1 font-body-sm text-[12px] text-on-surface-variant/80">Kein KI-Vorschlag — Allergene antippen oder „Keine Allergene" wählen.</p>
        )}

        {/* Volles 14er-Raster mit Namen; ausgewählt = Accent (+ ✨ bei KI-Vorschlag). */}
        <div className="mt-3 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          {LMIV.map(({ code, label }) => {
            const on = selected.includes(code);
            const suggested = sugCodes.includes(code);
            return (
              <button key={code} type="button" onClick={() => toggleFocusPick(cur, code)}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left font-body-sm text-[12px] transition-colors ${
                  on ? "border-primary bg-primary/10 text-primary" : "border-outline-variant/50 text-on-surface-variant hover:border-primary/40 dark:border-outline/40"
                }`}>
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded font-label-md text-[11px] font-bold ${on ? "bg-primary text-on-primary" : "bg-surface-container-high text-on-surface-variant/70 dark:bg-white/[0.08]"}`}>{code}</span>
                <span className="truncate font-medium">{label}</span>
                {suggested && <span className="ml-auto text-[11px] text-secondary" title="KI-Vorschlag">✨</span>}
              </button>
            );
          })}
        </div>

        {sug?.reason && (
          <p className="mt-3 font-body-sm text-[12px] text-on-surface-variant/80">
            <strong>{sugCodes.map((c) => lmivLabel(c)).join(", ")}:</strong> {sug.reason} — ersetzt keine Rechtsberatung, bitte prüfen.
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <Button className="disabled:opacity-40" disabled={selected.length === 0} onClick={() => focusAllergenConfirm(cur)}>
            <Icon name="check" className="text-[17px]" /> {selected.length > 0 ? `${selected.length} Allergen${selected.length === 1 ? "" : "e"} übernehmen` : "Allergene wählen"} <kbd className="ml-1 rounded bg-white/20 px-1 text-[11px]">↵</kbd>
          </Button>
          <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-secondary/50 bg-secondary/10 px-3 py-1.5 font-label-md text-label-md font-semibold text-secondary transition-colors hover:bg-secondary/20 dark:text-[#b8ae9e]" onClick={() => focusAllergenNone(cur)}>
            <Icon name="check_circle" className="text-[16px]" /> Keine Allergene <kbd className="ml-1 rounded bg-black/10 px-1 text-[11px] dark:bg-white/10">K</kbd>
          </button>
        </div>
      </div>
    );
  }

  // --- Geführter Ablauf: Queue der aktuellen Phase (spell ODER allergens) ---
  const guidedQueue: FocusTask[] = (() => {
    if (reviewPhase === "spell") return openSpellSuggestions().map((sug) => ({ kind: "spell", sug }));
    if (reviewPhase === "allergens") {
      const t: FocusTask[] = [];
      menu?.sections.forEach((s, si) => s.items.forEach((it, ii) => { if (it.allergens === null) t.push({ kind: "allergen", si, ii }); }));
      return t;
    }
    return [];
  })();
  /** Vor jeder gefuehrten Aktion den aktuellen Stand sichern (nur im gefuehrten Ablauf, nicht im Modal). */
  function snapshotGuided() {
    if (!menu || focusOpen || reviewPhase === "list") return;
    setGuidedHistory((h) => [...h, { menu: structuredClone(menu), spellSugs, allergenSugs, guidedPos, reviewPhase }]);
  }
  function guidedSkip() { snapshotGuided(); setFocusPick(null); setGuidedPos((p) => p + 1); }
  /** Kann ueberhaupt zurueck (Undo vorhanden, uebersprungene Position davor, oder Phase davor)? */
  const canGoBack =
    guidedHistory.length > 0 || guidedPos > 0 || (reviewPhase === "allergens" && (focusSpellCount > 0 || spellFailed));
  /**
   * Zurueck: macht die letzte gefuehrte Aktion rueckgaengig - auch ein bereits abgeschicktes
   * Gericht (Allergen-/Schreibkorrektur), ueber Positions- und Phasengrenzen hinweg.
   */
  function guidedBack() {
    setFocusPick(null);
    if (guidedHistory.length > 0) {
      const prev = guidedHistory[guidedHistory.length - 1]!;
      setGuidedHistory((h) => h.slice(0, -1));
      setMenu(prev.menu);
      setSpellSugs(prev.spellSugs);
      setAllergenSugs(prev.allergenSugs);
      setSpellFailed(false);
      setReviewPhase(prev.reviewPhase);
      setGuidedPos(prev.guidedPos);
      return;
    }
    if (guidedPos > 0) { setGuidedPos((p) => p - 1); return; }
    if (reviewPhase === "allergens") { setGuidedPos(0); setReviewPhase("spell"); }
  }
  /** Phase abschließen/ueberspringen -> naechste Phase bzw. volle Karte. */
  function guidedNextPhase() {
    setFocusPick(null);
    setGuidedPos(0);
    if (reviewPhase === "spell") {
      const allergenOpen = menu?.sections.some((s) => s.items.some((it) => it.allergens === null)) ?? false;
      setReviewPhase(allergenOpen ? "allergens" : "list");
    } else {
      setReviewPhase("list");
    }
  }
  /**
   * „Alle Vorschläge übernehmen" im geführten Schritt: alle offenen Vorschläge der AKTUELLEN
   * Phase auf einmal übernehmen und weiter. Snapshot vorher, damit „Zurück" alles rückgängig
   * macht (spiegelt „Alle überspringen", nur mit Übernahme statt Verwerfen).
   *
   * Galt bis 31.07.2026 nur für die Rechtschreibung. Antoine sass beim Import der Getraenkekarte
   * bei „53 von 146" und musste jede Position einzeln bestaetigen — bei 146 Positionen ist das
   * eine Zumutung, und der einzige Ausweg war „Alle überspringen", das NICHTS uebernimmt und alle
   * Positionen auf `null` (ungepflegt) stehen laesst. Uebernehmen ist auch rechtlich das bessere
   * Ende: danach steht ein geprueftes Ergebnis da statt gar keins.
   *
   * WICHTIG: Uebernommen wird nur, wofuer es einen Vorschlag GIBT. Positionen ohne Vorschlag
   * bleiben ungeklaert — es wird nichts erfunden, nur Vorhandenes bestaetigt.
   */
  function guidedAcceptAll() {
    snapshotGuided();
    if (reviewPhase === "spell") applyAllSpell();
    else applyAllAllergen();
    guidedNextPhase();
  }

  // Tastatur: ↵ Übernehmen · K Keine · → Überspringen · Esc Schließen.
  useEffect(() => {
    if (!focusOpen) return;
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.key === "Escape") { setFocusOpen(false); return; }
      const q = focusQueue;
      const cur = q.length ? q[focusPos % q.length]! : null;
      if (!cur) return;
      if (e.key === "Enter") {
        e.preventDefault();
        if (cur.kind === "spell") focusSpellApply(cur);
        else focusAllergenConfirm(cur);
      } else if (e.key === "k" || e.key === "K") {
        if (cur.kind === "allergen") { e.preventDefault(); focusAllergenNone(cur); }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        focusSkip();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusOpen, focusPos, focusTab, focusPick, menu, spellSugs, allergenSugs]);

  // Baseline (fuer „X von Y") bei Öffnen/Tab-Wechsel auf die aktuelle offene Anzahl setzen.
  useEffect(() => {
    if (!focusOpen) return;
    setFocusBaseline(focusTab === "spell" ? focusSpellCount : focusTab === "allergen" ? focusAllergenCount : focusSpellCount + focusAllergenCount);
    setFocusPos(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusOpen, focusTab]);

  // „X von Y" der aktuellen geführten Phase.
  useEffect(() => {
    if (reviewPhase === "spell") setGuidedBaseline(openSpellSuggestions().length);
    else if (reviewPhase === "allergens") setGuidedBaseline(menu?.sections.reduce((n, s) => n + s.items.filter((it) => it.allergens === null).length, 0) ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewPhase]);

  /**
   * 16c: baut das final zu committende Payload SYNCHRON aus dem aktuellen menu — offene
   * Rechtschreib- und Allergen-Vorschlaege werden angewandt, optional die dann noch
   * ungeklaerten Positionen als „keine Allergene" ([]) bestaetigt. Kein Umweg ueber setMenu
   * (das waere im selben Tick veraltet), damit der Commit garantiert den Endstand schickt.
   */
  function buildFinalMenu(confirmRestNone: boolean): ParsedMenu | null {
    if (!menu) return null;
    const m: ParsedMenu = structuredClone(menu);
    for (const sug of openSpellSuggestions()) {
      if (sug.itemIndex === null) { const s = m.sections[sug.sectionIndex]; if (s) s.title = sug.corrected; }
      else { const it = m.sections[sug.sectionIndex]?.items[sug.itemIndex]; if (it) { if (sug.field === "name") it.name = sug.corrected; else it.description = sug.corrected; } }
    }
    for (const sug of openAllergenSuggestions()) {
      const it = m.sections[sug.sectionIndex]?.items[sug.itemIndex];
      if (it && it.allergens === null) it.allergens = [...sug.codes];
    }
    if (confirmRestNone) for (const s of m.sections) for (const it of s.items) if (it.allergens === null) it.allergens = [];
    return m;
  }

  /** Zahlen fuer den Abschluss-Dialog: was passiert beim Uebernehmen. */
  const finishStats = (() => {
    const spellN = openSpellSuggestions().length;
    const allergenN = openAllergenSuggestions().length;
    const unresolved = menu?.sections.reduce((n, s) => n + s.items.filter((i) => i.allergens === null).length, 0) ?? 0;
    const restNull = Math.max(0, unresolved - allergenN); // ungeklaert OHNE Vorschlag
    return { spellN, allergenN, restNull };
  })();

  /** Bestaetigung aus dem Abschluss-Dialog: wendet Vorschlaege an + waehlt den Umgang mit Offenen. */
  async function confirmFinish() {
    if (finishStats.restNull > 0 && draftChoice === "confirm") {
      await commit({ payload: buildFinalMenu(true) ?? undefined });
    } else if (finishStats.restNull > 0 && draftChoice === "draft") {
      await commit({ payload: buildFinalMenu(false) ?? undefined, holdUnresolved: true });
    } else {
      // Keine offenen ohne Vorschlag: Vorschlaege anwenden und voll committen.
      await commit({ payload: buildFinalMenu(false) ?? undefined });
    }
  }

  async function commit(opts?: { payload?: ParsedMenu; holdUnresolved?: boolean }) {
    if (!job || !menu) return;
    const payload = opts?.payload ?? menu;
    // Uebersetzungs-Schutz: wurde eine wahrscheinliche Sprachversion erkannt und NICHT
    // explizit abgelehnt, vor dem normalen Import nachfragen - sonst entstehen aus einer
    // EN-Karte 80+ eigenstaendige Duplikat-Positionen.
    if (translationCandidate && !detectionDismissed) {
      const ok = await bestaetigen({
        titel: "Trotzdem als eigenständige Positionen importieren?",
        text: `KI-Empfehlung: Diese Karte scheint die ${LANG_NAME[job.sourceLang ?? ""] ?? "fremdsprachige"} Version von „${translationCandidate.card.name}“ zu sein - empfohlen ist „Als Übersetzung zuordnen“, NICHT der Neu-Import.`,
        bestaetigen: "Trotzdem importieren",
      });
      if (!ok) return;
    }
    // P1 (Speisekarten): Ziel-Karte validieren, BEVOR der Commit losgeht.
    if (cardMode === "create" && !cardName.trim()) {
      setError("Bitte einen Namen für die neue Karte angeben – oder Modus »Ohne Karte« wählen.");
      return;
    }
    if (cardMode === "create" && kartenDerArt.length > 0 && kartenartAntwort === null) {
      setError("Bitte bei der Kartenart wählen: bestehende Karte ersetzen oder zusätzliche Karte anlegen.");
      return;
    }
    if ((cardMode === "append" || cardMode === "replace") && !appendCardId) {
      setError("Bitte eine bestehende Karte auswählen – oder Modus »Ohne Karte« wählen.");
      return;
    }
    // P2: Ersetzen ist ein gewichtiger Eingriff — explizit bestätigen (mit Klartext-Folgen).
    if (cardMode === "replace") {
      const target = existingCards.find((k) => k.id === appendCardId);
      const ok = await bestaetigen({
        titel: `Karte „${target?.name ?? "?"}“ durch diesen Import ERSETZEN?`,
        text: "Positionen und Kategorien, die im neuen Import fehlen, werden archiviert (nicht gelöscht – Angebote und Pakete behalten ihre Referenzen).",
        bestaetigen: "Ersetzen",
      });
      if (!ok) return;
    }
    setBusy(true);
    setError(null);
    try {
      const card =
        cardMode === "create"
          ? { mode: "create" as const, name: cardName.trim(), kind: cardKind, ...(cardTypeId ? { cardTypeId } : {}) }
          : cardMode === "append" || cardMode === "replace"
            ? { mode: cardMode, cardId: appendCardId }
            : undefined;
      const res = await apiFetch(`/api/menu-imports/${job.id}/commit`, {
        method: "POST",
        body: JSON.stringify({ parsedPayload: payload, ...(card ? { card } : {}), ...(opts?.holdUnresolved ? { holdUnresolved: true } : {}) }),
      });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { data: CommitSummary & { cardId?: string | null; partial?: boolean; draftCount?: number } };
      // P1b: Ergaenzen/Ersetzen einer Bestandskarte OHNE Kartenart ordnet sie der gewaehlten Art zu.
      // Eine Karte mit anderer Art wird nie stillschweigend umgehaengt.
      const ziel = card && card.mode !== "create" ? existingCards.find((k) => k.id === card.cardId) : undefined;
      if (cardTypeId && ziel && !ziel.cardTypeId) {
        await apiMutate(`/api/menu-cards/${ziel.id}`, "PATCH", { cardTypeId }).catch(() => undefined); // ponytail: best effort, Import ist schon durch
        cardsQ.reload();
      }
      setFinishOpen(false);
      if (body.data.partial) {
        // 16c „Als Entwurf behalten": geprueft ging live, die ungeklaerten bleiben hier als
        // Entwurf. In der Review bleiben, nur noch die offenen zeigen; nichts geht verloren.
        const draft = opts?.payload ? { ...opts.payload, sections: opts.payload.sections.map((s) => ({ ...s, items: s.items.filter((i) => i.allergens === null) })).filter((s) => s.items.length > 0) } : menu;
        setMenu(draft);
        setSpellSugs([]);
        setAllergenSugs([]);
        // Der Filter oben verschiebt Section-/Item-Indizes - eingefrorene Aehnlichkeits-Hinweise
        // (keyed nach altem si/ii) waeren sonst falsch zugeordnet.
        setSimilaritySugs([]);
        setAllergensConfirmed(false);
        // Folge-Commits gehen auf die eben befuellte Karte (append), nicht neu anlegen.
        if (body.data.cardId) { setCardMode("append"); setAppendCardId(body.data.cardId); }
        setPartialNotice(body.data.draftCount ?? draft.sections.reduce((n, s) => n + s.items.length, 0));
        return;
      }
      setSummary(body.data);
      setStep("done");
    } catch (e) {
      // Reale Fehlermeldung durchreichen (nicht erfinden).
      setError(importError(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Verankertes Uebersetzungs-Matching: schickt die geparste fremdsprachige Karte ans
   * Backend, das Modell ordnet sie den bestehenden Positionen der Ziel-Karte zu.
   * Ergebnis ist IMMER ein Paar-Review — nichts wird ohne Bestaetigung geschrieben.
   */
  async function startTranslationMatch(card: MenuCard, lang: string) {
    if (!job) return;
    setMatchBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/menu-cards/${card.id}/match-translation`, {
        method: "POST",
        body: JSON.stringify({ lang, importId: job.id }),
      });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as {
        data: { lang: string; pairs: { targetId: string; targetName: string; targetPriceCents: number; name: string; description: string | null; confidence: number }[]; totalItems: number; notFound: number };
      };
      setPairReview({
        cardId: card.id,
        cardName: card.name,
        lang: body.data.lang,
        pairs: body.data.pairs.map((p) => ({ ...p, include: true })),
        notFound: body.data.notFound,
        totalItems: body.data.totalItems,
      });
      setDetectionDismissed(true);
    } catch (e) {
      setError(importError(e instanceof Error ? e.message : String(e)));
    } finally {
      setMatchBusy(false);
    }
  }

  /** Schreibt NUR die bestaetigten Paare als Uebersetzung auf die Ziel-Karte. */
  async function applyTranslation() {
    if (!pairReview || !job) return;
    const included = pairReview.pairs.filter((p) => p.include);
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/menu-cards/${pairReview.cardId}/apply-translation`, {
        method: "POST",
        body: JSON.stringify({
          lang: pairReview.lang,
          importId: job.id,
          pairs: included.map((p) => ({ targetId: p.targetId, name: p.name, description: p.description })),
        }),
      });
      if (!res.ok) throw await toApiError(res);
      const body = (await res.json()) as { data: { translatedItems: number } };
      setSummary({
        createdItems: 0,
        updatedItems: 0,
        createdCategories: 0,
        totalItems: pairReview.totalItems,
        createdPackages: 0,
        skippedPackages: 0,
        cardId: pairReview.cardId,
        mode: "translate",
        lang: pairReview.lang,
        translatedItems: body.data.translatedItems,
        translatedCategories: 0,
        skipped: pairReview.notFound + (pairReview.pairs.length - included.length),
      });
      setStep("done");
    } catch (e) {
      setError(importError(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  const itemCount = menu?.sections.reduce((n, s) => n + s.items.length, 0) ?? 0;
  // Weiche relevant, sobald eine wahrscheinliche Sprachversion erkannt wurde ODER die
  // Quellsprache fremd ist und es ueberhaupt Karten zum Zuordnen gibt.
  const gateRelevant =
    translationCandidate != null || (job?.sourceLang != null && job.sourceLang !== "de" && existingCards.length > 0);
  const gateOpen = step === "review" && !pairReview && gateRelevant && gateChoice === "pending";

  // Geführter Ablauf: leert sich die aktuelle Phase, automatisch zur naechsten Phase / vollen Karte.
  useEffect(() => {
    if (step !== "review" || gateOpen || pairReview) return;
    // Gescheiterte Rechtschreibpruefung NICHT automatisch verlassen - der Retry-Knopf soll stehen.
    if (reviewPhase === "spell" && openSpellSuggestions().length === 0 && !spellFailed) {
      const allergenOpen = menu?.sections.some((s) => s.items.some((it) => it.allergens === null)) ?? false;
      setReviewPhase(allergenOpen ? "allergens" : "list");
      setGuidedPos(0);
    } else if (reviewPhase === "allergens" && !(menu?.sections.some((s) => s.items.some((it) => it.allergens === null)) ?? false)) {
      setReviewPhase("list");
      setGuidedPos(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewPhase, spellSugs, menu, step, gateOpen, pairReview, spellFailed]);

  // Tastatur im geführten Ablauf (kein Modal): ↵ Übernehmen · K Keine · → Überspringen.
  useEffect(() => {
    if (step !== "review" || reviewPhase === "list" || focusOpen || gateOpen || pairReview) return;
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      const q = guidedQueue;
      const cur = q.length ? q[guidedPos % q.length]! : null;
      if (!cur) return;
      if (e.key === "Enter") {
        e.preventDefault();
        if (cur.kind === "spell") focusSpellApply(cur);
        else focusAllergenConfirm(cur);
      } else if ((e.key === "k" || e.key === "K") && cur.kind === "allergen") {
        e.preventDefault();
        focusAllergenNone(cur);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        guidedSkip();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        guidedBack();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, reviewPhase, focusOpen, gateOpen, pairReview, guidedPos, focusPick, menu, spellSugs, allergenSugs]);

  // Design 15: Stepper-Phase 1-4. Waehrend der Analyse gilt Schritt 1 noch als aktiv.
  const stepperPhase: 1 | 2 | 3 | 4 = step === "upload" || step === "parsing" ? 1 : step === "done" ? 4 : gateOpen ? 2 : 3;
  // Tri-State (H-02): jede Position mit ungeklaerten Allergenen (null) muss einzeln
  // aufgeloest werden (Codes waehlen oder "keine Allergene" bestaetigen), sonst kein Commit.
  const unresolvedAllergens = menu?.sections.reduce((n, s) => n + s.items.filter((i) => i.allergens === null).length, 0) ?? 0;

  return (
    <>
      {bestaetigungsDialog}
      {/* Design 15: zentrierte Buehne. Titel + kontext-abhaengiger Untertitel + 4-Schritt-Stepper.
          Der Fertig-Schritt hat seinen eigenen Kopf (nur Stepper), siehe unten. */}
      <div className="relative">
        {/* Punkt 115: gemeinsamer Rueckweg statt des am Handy ausgeblendeten Knopfs „Zum Katalog“. */}
        <Rueckweg eltern={{ label: "Katalog", to: "/katalog" }} aktuell="Karte importieren" />
        <div className="mx-auto mb-8 max-w-2xl pt-1 text-center">
          {step !== "done" && (
            <>
              <h1 className="font-headline-md text-headline-md font-bold text-on-background dark:text-inverse-on-surface">
                Karte importieren
              </h1>
              <p className="mt-1.5 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                {deepLinkBusy
                  ? "Dein Import wird geladen — der Review wird gleich vorbereitet."
                  : step === "upload"
                  ? "Aus deiner Speise- oder Getränkekarte wird ein fertiger, rechtssicherer Katalog — statt einem Nachmittag Abtippen in rund 30 Sekunden."
                  : step === "parsing"
                    ? "Die KI liest deine Karte … das dauert meist unter einer Minute."
                    : pairReview
                      ? `Die KI hat die ${LANG_NAME[pairReview.lang] ? LANG_NAME[pairReview.lang] + "n" : pairReview.lang.toUpperCase() + "-"} Texte den bestehenden Positionen zugeordnet.`
                      : gateOpen && translationCandidate
                        ? `${translationCandidate.overlap} der ${itemCount} Positionen stimmen mit einer vorhandenen Karte überein.`
                        : gateOpen
                          ? `Fremdsprachige Karte erkannt (${(job?.sourceLang ?? "").toUpperCase()}).`
                          : `${itemCount} Positionen in ${menu?.sections.length ?? 0} Kategorien erkannt.`}
              </p>
              <div className="mt-4">
                <Stepper phase={stepperPhase} />
              </div>
            </>
          )}
          {step === "done" && <Stepper phase={4} />}
        </div>
      </div>

      {/* Ein einziges verstecktes File-Input für Dropzone + Recovery-Aktionen. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/*"
        multiple
        className="hidden"
        onChange={(e) => acceptFiles(e.target.files)}
      />

      {/* Drag-anywhere-Overlay: erscheint, sobald irgendwo auf der Seite eine Datei gezogen wird. */}
      {step === "upload" && pageDragActive && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/45 p-8 backdrop-blur-[3px]">
          <div className="pointer-events-none flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-primary bg-surface-container-lowest/95 px-16 py-12 text-center shadow-xl dark:bg-inverse-surface/95">
            <Icon name="upload_file" className="text-5xl text-primary" />
            <p className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">
              Datei hier ablegen
            </p>
            <p className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
              PDF, JPG oder PNG · max. 8 MB je Datei
            </p>
          </div>
        </div>
      )}

      {/* A3: Ladeanzeige, während ein deep-verlinkter Import (?importId=) geholt wird. */}
      {deepLinkBusy && (
        <div className="mx-auto max-w-lg">
          <Card className="flex flex-col items-center gap-4 p-8 text-center sm:p-10">
            <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-primary/20 border-t-primary" />
            <div>
              <p className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
                Import wird geladen …
              </p>
              <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                Die geprüfte Karte wird für den Review vorbereitet.
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* -------- Upload (Design 15a): zentrierte Karte, eine Aktion -------- */}
      {step === "upload" && !deepLinkBusy && (
        <div className={parseFailed ? "mx-auto max-w-xl space-y-4" : "mx-auto grid max-w-4xl gap-5 lg:grid-cols-[1fr_300px] lg:items-stretch"}>
          {parseFailed ? (
            /* Fehlerzustand: Fehler sitzt an der Datei, alle Optionen an EINER Stelle. */
            <Card className="space-y-4 p-5">
              {files.length > 0 && (
                <div className="flex items-center gap-3 rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-3 dark:border-outline/30 dark:bg-surface-variant/10">
                  <Icon name="description" className="text-2xl text-on-surface-variant" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                      {files[0]!.name}
                    </p>
                    <p className="font-body-sm text-body-sm text-on-surface-variant/70">
                      {files[0]!.type === "application/pdf" ? "PDF" : "Bild"} · {formatFileSize(files[0]!.size)}
                    </p>
                  </div>
                </div>
              )}
              <div className="rounded-xl border border-error/40 bg-error-container/70 p-5 dark:border-error/30 dark:bg-error/10">
                <div className="flex items-start gap-3">
                  <Icon name="error" className="text-2xl text-error dark:text-[#e39b92]" />
                  <div className="space-y-1">
                    <p className="font-body-md text-body-md font-semibold text-error dark:text-[#e39b92]">
                      Analyse fehlgeschlagen
                    </p>
                    <p className="font-body-sm text-body-sm text-error/90 dark:text-[#e39b92]/80">
                      {error ?? "Analyse fehlgeschlagen."}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button onClick={handleAnalyzeClick}>
                    <Icon name="refresh" className="text-[18px]" /> Erneut analysieren
                  </Button>
                  <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
                    <Icon name="image" className="text-[18px]" /> Anderes Foto wählen
                  </Button>
                  <Button variant="secondary" onClick={() => navigate("/katalog")}>
                    <Icon name="edit" className="text-[18px]" /> Manuell im Katalog anlegen
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex justify-center gap-2 lg:justify-start">
                <button type="button" className={tabClass(srcTab === "file")} onClick={() => setSrcTab("file")}>
                  <Icon name="upload_file" className="text-[16px]" /> PDF oder Foto
                </button>
                <button type="button" className={tabClass(srcTab === "text")} onClick={() => setSrcTab("text")}>
                  <Icon name="notes" className="text-[16px]" /> Text einfügen
                </button>
              </div>

              <Card className="flex flex-1 flex-col gap-3 p-4">
                {srcTab === "file" ? (
                  <>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => fileInputRef.current?.click()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          fileInputRef.current?.click();
                        }
                      }}
                      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                      onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                      onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                      onDrop={(e) => { e.preventDefault(); setDragActive(false); acceptFiles(e.dataTransfer.files); }}
                      className={`flex min-h-[200px] flex-1 cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed py-10 text-center transition-colors ${
                        dragActive || pageDragActive
                          ? "border-primary bg-primary/5"
                          : "border-outline-variant/60 hover:border-primary/50 dark:border-outline/40"
                      }`}
                    >
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-surface-container-high dark:bg-white/[0.08]">
                        <Icon name="upload" className="text-[22px] text-on-surface-variant" />
                      </span>
                      <div>
                        <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                          Karte hierher ziehen
                        </p>
                        <p className="mt-0.5 font-body-sm text-[12px] text-on-surface-variant/70">
                          PDF, JPG oder PNG · mehrere Seiten möglich · max. 8 MB
                        </p>
                      </div>
                      <ButtonOptik variant="secondary" className="pointer-events-none">Datei wählen</ButtonOptik>
                    </div>

                    {files.map((f, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-xl border border-outline-variant/40 px-3.5 py-2.5 dark:border-outline/30">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-container-high dark:bg-white/[0.08]">
                          <Icon name={f.type === "application/pdf" ? "description" : "image"} className="text-[17px] text-on-surface-variant" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                            {f.name}
                          </p>
                          <p className="font-body-sm text-[12px] text-on-surface-variant/70">
                            {f.type === "application/pdf" ? "PDF" : "Bild"} · {formatFileSize(f.size)}
                          </p>
                        </div>
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#F0EAE0] px-2 py-0.5 font-label-md text-[11px] font-semibold text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                          <span className="h-1.5 w-1.5 rounded-full bg-secondary dark:bg-secondary-fixed-dim" /> Bereit
                        </span>
                        <Button
                          variant="ghost"
                          className="!px-1.5 text-on-surface-variant"
                          title="Datei entfernen"
                          onClick={() => setFiles((prev) => prev.filter((_, k) => k !== i))}
                        >
                          <Icon name="close" className="text-[16px]" />
                        </Button>
                      </div>
                    ))}
                  </>
                ) : (
                  <Textarea
                    className="min-h-[200px] flex-1"
                    placeholder="Karte als Text einfügen (z. B. aus einer E-Mail) …"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                )}
              </Card>

              {/* Nicht-fatale Hinweise (z. B. Datei zu groß) — inline unter der Karte. */}
              {error && (
                <p className="flex items-center justify-center gap-1.5 font-body-sm text-body-sm text-error dark:text-[#e39b92]">
                  <Icon name="error" className="text-[16px]" /> {error}
                </p>
              )}

              <Button className="w-full justify-center !py-3" onClick={handleAnalyzeClick}>
                <Icon name="auto_awesome" className="text-[18px]" /> Karte analysieren
              </Button>
              <p className="flex items-center justify-center gap-1.5 font-body-sm text-[12px] text-on-surface-variant/70 lg:justify-start">
                <Icon name="lock" className="text-[14px]" /> Nichts wird veröffentlicht, bevor du es freigibst.
              </p>
            </div>
          )}
          {/* 17a rechte Spalte: „Das übernimmt MAESTRO für dich" — verkauft die KI-Leistung. */}
          {!parseFailed && (
            <Card as="aside" className="space-y-1 self-start p-5">
              <p className="font-label-md text-[11px] font-semibold uppercase tracking-wide text-primary">
                <Icon name="auto_awesome" className="mr-1 align-[-2px] text-[14px]" /> Das übernimmt MAESTRO für dich
              </p>
              <div className="mt-3 space-y-3.5">
                {[
                  { icon: "restaurant_menu", title: "Alles automatisch erkannt", body: "Gerichte, Getränke, Preise, Kategorien und ganze Menüs — sauber strukturiert aus PDF oder Foto." },
                  { icon: "spellcheck", title: "Rechtschreibung & Schreibweise", body: "Tippfehler werden korrigiert, Schreibweisen vereinheitlicht — mit einem Klick übernommen." },
                  { icon: "verified", title: "Allergene nach LMIV", body: "MAESTRO schlägt die passenden Allergene je Position vor — auch für Getränke. Du bestätigst — rechtssicher." },
                  { icon: "translate", title: "Sprachversionen verknüpft", body: "Englische oder weitere Fassungen werden erkannt und als Übersetzung zugeordnet." },
                ].map((v) => (
                  <div key={v.title} className="flex gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon name={v.icon} className="text-[17px]" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{v.title}</p>
                      <p className="mt-0.5 font-body-sm text-[12px] leading-snug text-on-surface-variant/80">{v.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* §5.8: Was frueher hochgeladen wurde und NIE im Katalog ankam. Bewusst hier — auf genau
          der Seite, auf der der Betrieb seine Karten einliest, und nicht in einem Wartungsmenue,
          das niemand oeffnet. Auf Prod betrifft das 11 Dokumente, die bisher nirgends auftauchten. */}
      {step === "upload" && !deepLinkBusy && (
        <div className="mx-auto mt-5 max-w-4xl">
          <NichtEingeleseneDokumente dokumente={fehlgeschlageneQ.data ?? []} />
        </div>
      )}

      {/* -------- Parsing / Analyse-Moment (17b): Fortschritts-Ring + Live-Checkliste -------- */}
      {step === "parsing" && (
        <div className="mx-auto max-w-lg">
          <Card className="space-y-6 p-6 sm:p-8">
            {(() => {
              const doneN = analysisSteps.filter((s) => s === "done" || s === "failed").length;
              const pct = Math.round(((doneN + (analysisSteps.some((s) => s === "running") ? 0.5 : 0)) / ANALYSIS_STAGES.length) * 100);
              return (
                <div className="flex flex-col items-center gap-4 text-center">
                  {/* Fortschritts-Ring — echter Fortschritt (abgeschlossene Schritte) */}
                  <div className="relative h-28 w-28">
                    <svg viewBox="0 0 100 100" className="h-28 w-28 -rotate-90">
                      <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" className="stroke-primary/15" />
                      <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" strokeLinecap="round"
                        className="stroke-primary transition-[stroke-dashoffset] duration-500 ease-out"
                        style={{ strokeDasharray: 263.9, strokeDashoffset: 263.9 * (1 - pct / 100) }} />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">{pct}%</span>
                  </div>
                  <div>
                    <p className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">KI-Analyse läuft</p>
                    <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      {files[0]?.name ? <>„{files[0]!.name}" wird gelesen.</> : "Deine Karte wird gelesen."} Gleich prüfst du alles, bevor etwas gespeichert wird.
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Echte Checkliste: jeder Punkt entspricht einem realen KI-Schritt. */}
            <ul className="space-y-2.5">
              {ANALYSIS_STAGES.map((s, i) => {
                const st = analysisSteps[i]!;
                const count = analysisCounts[i];
                return (
                  <li
                    key={s.label}
                    className={`flex items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors ${
                      st === "done" ? "border-secondary/30 bg-secondary/5"
                        : st === "failed" ? "border-secondary/40 bg-secondary/5"
                        : st === "running" ? "border-primary/40 bg-primary/5"
                        : "border-outline-variant/40 dark:border-outline/30"
                    }`}
                  >
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                      st === "done" ? "bg-[#F0EAE0] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"
                        : st === "failed" ? "bg-secondary/15 text-secondary dark:text-[#b8ae9e]"
                        : st === "running" ? "bg-primary/15 text-primary"
                        : "bg-surface-container-high text-on-surface-variant/50 dark:bg-white/[0.08]"
                    }`}>
                      {st === "done" ? <Icon name="check" className="text-[16px]" />
                        : st === "failed" ? <Icon name="warning" className="text-[15px]" />
                        : st === "running" ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary/25 border-t-primary" />
                        : <Icon name={s.icon} className="text-[15px]" />}
                    </span>
                    <span className={`font-body-sm text-body-sm ${st === "pending" ? "text-on-surface-variant/70" : "font-semibold text-on-background dark:text-inverse-on-surface"}`}>
                      {s.label}
                    </span>
                    {st === "done" && <span className="ml-auto font-label-md text-[11px] font-semibold text-secondary dark:text-[#b8ae9e]">{count != null ? `${count} ${s.unit}` : "fertig"}</span>}
                    {st === "running" && <span className="ml-auto font-label-md text-[11px] font-semibold text-primary">läuft …</span>}
                    {st === "failed" && <span className="ml-auto font-label-md text-[11px] font-semibold text-secondary dark:text-[#b8ae9e]">übersprungen</span>}
                  </li>
                );
              })}
            </ul>

            <div className="flex items-center justify-between gap-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
              <p className="flex items-center gap-1.5 font-body-sm text-[12px] text-on-surface-variant/80">
                <Icon name="lock" className="text-[14px]" /> Deine Daten bleiben privat — nichts wird ohne deine Freigabe veröffentlicht.
              </p>
              <Button
                variant="ghost"
                className="shrink-0 text-on-surface-variant"
                onClick={() => { parseRunRef.current++; setStep("upload"); }}
              >
                Abbrechen
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* -------- Review -------- */}
      {/* -------- Paar-Review (Uebersetzung) — ersetzt die Sektions-Ansicht, solange aktiv -------- */}
      {step === "review" && pairReview && (
        <div className="mx-auto max-w-3xl space-y-4">
          {error && <ErrorNote message={error} />}
          <div className="text-center">
            <h2 className="font-title-lg text-title-lg font-bold text-on-background dark:text-inverse-on-surface">
              Übersetzung prüfen: „{pairReview.cardName}“
            </h2>
            <p className="mx-auto mt-1 max-w-md font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
              Prüfe die Paare, korrigiere bei Bedarf die übersetzte Fassung — Preise, Allergene und Struktur bleiben
              unverändert.
            </p>
          </div>
          <TranslationPairReview
            pairs={pairReview.pairs}
            lang={pairReview.lang}
            notFound={pairReview.notFound}
            onChange={(pairs) => setPairReview((prev) => (prev ? { ...prev, pairs } : prev))}
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button variant="secondary" onClick={() => setPairReview(null)}>
              <Icon name="chevron_left" className="text-[18px]" /> Zurück
            </Button>
            <Button
              disabled={busy || pairReview.pairs.filter((p) => p.include).length === 0}
              onClick={() => void applyTranslation()}
            >
              {busy ? "Übernimmt …" : `${pairReview.pairs.filter((p) => p.include).length} Übersetzungen übernehmen`}{" "}
              <Icon name="arrow_forward" className="text-[16px]" />
            </Button>
          </div>
        </div>
      )}

      {/* -------- Zuordnungs-Weiche (Design 15b): ERST entscheiden, DANN pruefen -------- */}
      {gateOpen && menu && (
        <div className="mx-auto max-w-xl space-y-4">
          {error && <ErrorNote message={error} />}
          <div className="text-center">
            <h2 className="font-title-lg text-title-lg font-bold text-on-background dark:text-inverse-on-surface">
              Wohin gehört diese Karte?
            </h2>
            <p className="mx-auto mt-1 max-w-md font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
              Ordnest du sie als Übersetzung zu, bleiben Preise, Allergene und Struktur unverändert — du bestätigst nur
              die Texte.
            </p>
          </div>

          {/* KI-Empfehlung: die EINE hervorgehobene Aktion. */}
          {translationCandidate && (
            <div className="rounded-xl border border-primary/40 bg-primary/5 p-4">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-on-primary">
                  <Icon name="auto_awesome" className="text-[16px]" />
                </span>
                <span className="rounded bg-primary/15 px-1.5 py-0.5 font-label-md text-[11px] font-bold uppercase tracking-wide text-primary">
                  KI-Empfehlung
                </span>
                <span className="ml-auto font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
                  {Math.round(translationCandidate.score * 100)} % Preisübereinstimmung
                </span>
              </div>
              <p className="mt-3 font-body-md text-body-md font-bold text-on-background dark:text-inverse-on-surface">
                {LANG_NAME[job?.sourceLang ?? ""] ? LANG_NAME[job?.sourceLang ?? ""]!.charAt(0).toUpperCase() + LANG_NAME[job?.sourceLang ?? ""]!.slice(1) : "Fremdsprachige"} Version von „{translationCandidate.card.name}“
              </p>
              <p className="mt-0.5 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                {translationCandidate.overlap} von {itemCount} Positionen passen exakt zu einem Original der Karte.
              </p>
              <Button
                className="mt-3.5 w-full justify-center"
                disabled={matchBusy}
                onClick={() => {
                  setAppendCardId(translationCandidate.card.id);
                  void startTranslationMatch(translationCandidate.card, translateLang);
                }}
              >
                {matchBusy ? "Ordnet zu …" : "Als Übersetzung zuordnen"} <Icon name="arrow_forward" className="text-[16px]" />
              </Button>
            </div>
          )}

          {translationCandidate && (
            <div className="flex items-center gap-3">
              <span className="h-px flex-1 bg-outline-variant/40" />
              <span className="font-label-md text-[11px] uppercase tracking-wide text-on-surface-variant/70">oder</span>
              <span className="h-px flex-1 bg-outline-variant/40" />
            </div>
          )}

          {/* Andere Ziel-Karte + Sprache manuell waehlen. */}
          <Card className="space-y-3 p-4">
            <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              {translationCandidate ? "Zu einer anderen Karte zuordnen" : "Übersetzung einer bestehenden Karte?"}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-[200px] flex-1">
                <Select
                  value={gateManualCardId || KEINE_KARTE}
                  onValueChange={(v) => setGateManualCardId(v === KEINE_KARTE ? "" : v)}
                  options={[
                    { value: KEINE_KARTE, label: "Ziel-Karte wählen …" },
                    ...existingCards.map((k) => ({ value: k.id, label: `${k.name} (${k.itemCount ?? 0} Positionen)` })),
                  ]}
                />
              </div>
              <div className="w-[140px]" title="Sprache dieser Karte">
                <Select
                  value={translateLang}
                  onValueChange={setTranslateLang}
                  ariaLabel="Sprache dieser Karte"
                  options={[
                    { value: "en", label: "Englisch" },
                    { value: "it", label: "Italienisch" },
                    { value: "fr", label: "Französisch" },
                    { value: "es", label: "Spanisch" },
                  ]}
                />
              </div>
              <Button
                variant="secondary"
                disabled={matchBusy || !gateManualCardId}
                onClick={() => {
                  const card = existingCards.find((k) => k.id === gateManualCardId);
                  if (card) {
                    setAppendCardId(card.id);
                    void startTranslationMatch(card, translateLang);
                  }
                }}
              >
                {matchBusy ? "Ordnet zu …" : "Zuordnen"}
              </Button>
            </div>
          </Card>

          {/* Eigenstaendige Karte: klare dritte Option als klickbare Zeile. */}
          <Card
            type="button"
            as="button" className="flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-surface-container-high/40 dark:hover:bg-surface-variant/20"
            onClick={() => {
              setGateChoice("standalone");
              setDetectionDismissed(true);
            }}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-container-high dark:bg-white/[0.08]">
              <Icon name="menu_book" className="text-[17px] text-on-surface-variant" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                Das ist eine eigenständige Karte
              </span>
              <span className="block font-body-sm text-[12px] text-on-surface-variant/80">
                Alle {itemCount} Positionen als neue Einträge im Katalog anlegen.
              </span>
            </span>
            <Icon name="chevron_right" className="shrink-0 text-[20px] text-on-surface-variant/60" />
          </Card>

          <div className="text-center">
            <Button variant="ghost" className="font-body-sm text-body-sm text-on-surface-variant" onClick={() => navigate("/katalog")}>
              Abbrechen
            </Button>
          </div>
        </div>
      )}

      {/* -------- Geführter Ablauf (KISS): erst Rechtschreibung, dann Allergene, dann volle Karte -------- */}
      {step === "review" && menu && !pairReview && !gateOpen && reviewPhase !== "list" && (() => {
        const q = guidedQueue;
        const cur = q.length ? q[guidedPos % q.length]! : null;
        const done = Math.max(0, guidedBaseline - q.length);
        const isSpell = reviewPhase === "spell";
        return (
          <div className="mx-auto max-w-xl space-y-4">
            {error && <ErrorNote message={error} />}
            {/* Phasen-Kopf */}
            <div className="text-center">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-label-md text-[12px] font-semibold ${isSpell ? "bg-secondary/10 text-secondary dark:text-[#b8ae9e]" : "bg-secondary/10 text-secondary dark:text-[#b8ae9e]"}`}>
                <Icon name={isSpell ? "spellcheck" : "verified"} className="text-[15px]" />
                {isSpell ? "Schritt 1 · Rechtschreibung" : `Schritt ${focusSpellCount > 0 ? "2" : "1"} · Allergene`}
              </span>
              <h2 className="mt-2 font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
                {isSpell ? "Schreibweisen bestätigen" : "Allergene klären (LMIV)"}
              </h2>
              <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                {cur
                  ? `${done + 1} von ${guidedBaseline} — eins nach dem anderen. Danach siehst du die ganze Karte.`
                  : isSpell && spellFailed
                    ? "Die Rechtschreibprüfung konnte nicht abgeschlossen werden."
                    : "Fertig."}
              </p>
              {/* Fortschrittsbalken */}
              <div className="mx-auto mt-3 h-1.5 max-w-xs overflow-hidden rounded-full bg-surface-container-high dark:bg-white/[0.08]">
                <div className="h-full rounded-full bg-primary transition-[width] duration-300" style={{ width: `${guidedBaseline ? Math.round((done / guidedBaseline) * 100) : 100}%` }} />
              </div>
            </div>

            {/* Zentrierte Fokus-Karte */}
            <Card className="p-5 sm:p-6">
              {cur ? focusTaskBody(cur) : isSpell && spellFailed ? (
                <div className="flex flex-col items-center gap-3 py-6 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F0EAE0] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"><Icon name="error" className="text-xl" /></div>
                  <p className="font-body-sm text-body-sm text-on-surface-variant">
                    Die KI-Rechtschreibprüfung ist fehlgeschlagen (meist kurzzeitige Überlastung). Die Karte wurde <strong>nicht</strong> geprüft.
                  </p>
                  <Button disabled={enrichBusy} className="disabled:opacity-50" onClick={() => void requestSpellCheck()}>
                    <Icon name="refresh" className="text-[17px]" /> {enrichBusy ? "KI prüft …" : "Rechtschreibprüfung wiederholen"}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 py-6 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F0EAE0] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"><Icon name="check" className="text-xl" /></div>
                  <p className="font-body-sm text-body-sm text-on-surface-variant">Dieser Schritt ist erledigt.</p>
                </div>
              )}
            </Card>

            {/* Sticky-Footer: Fortschritt + Weiter/Überspringen */}
            <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-outline-variant/30 bg-surface-container-lowest/95 p-3 shadow-lg backdrop-blur dark:border-outline/30 dark:bg-inverse-surface/95">
              <span className="font-body-sm text-[12px] text-on-surface-variant/80">
                <kbd className="rounded bg-surface-container-high px-1 dark:bg-white/[0.08]">↵</kbd> Übernehmen · <kbd className="rounded bg-surface-container-high px-1 dark:bg-white/[0.08]">K</kbd> Keine · <kbd className="rounded bg-surface-container-high px-1 dark:bg-white/[0.08]">←</kbd> Zurück · <kbd className="rounded bg-surface-container-high px-1 dark:bg-white/[0.08]">→</kbd> Überspringen
              </span>
              <span className="flex flex-wrap items-center gap-2">
                {/* Zurück: macht die letzte Aktion rueckgaengig (auch abgeschickte Gerichte), ueber
                    Positions- und Phasengrenzen hinweg. Deaktiviert nur ganz am Anfang. */}
                <Button variant="ghost" className="text-on-surface-variant disabled:opacity-40" disabled={!canGoBack} onClick={guidedBack}>
                  <Icon name="arrow_back" className="text-[17px]" /> Zurück
                </Button>
                {/* Gilt fuer BEIDE gefuehrten Schritte (Rechtschreibung + Allergene). Die Zahl
                    steht bewusst im Text: Bei 146 offenen Positionen und 93 Vorschlaegen waere
                    „Alle annehmen" irrefuehrend — es bleiben 53 ungeklaert. */}
                {(() => {
                  const offen = isSpell ? openSpellSuggestions().length : openAllergenSuggestions().length;
                  if (offen === 0) return null;
                  return (
                    <Button onClick={guidedAcceptAll}>
                      <Icon name="done_all" className="text-[17px]" /> Alle {offen} Vorschläge übernehmen
                    </Button>
                  );
                })()}
                <Button variant="ghost" className="text-on-surface-variant" onClick={() => { setReviewPhase("list"); setGuidedPos(0); }}>
                  Alle überspringen → zur Karte
                </Button>
                {cur ? (
                  <Button variant="secondary" onClick={guidedSkip}>Überspringen →</Button>
                ) : (
                  <Button onClick={guidedNextPhase}>
                    {isSpell && (menu.sections.some((s) => s.items.some((it) => it.allergens === null))) ? "Weiter zu Allergene" : "Weiter zur Karte"} <Icon name="arrow_forward" className="text-[17px]" />
                  </Button>
                )}
              </span>
            </div>
          </div>
        );
      })()}

      {step === "review" && menu && !pairReview && !gateOpen && reviewPhase === "list" && (
        <div className="space-y-5">
          {error && <ErrorNote message={error} />}
          {/* 16a Prüf-Cockpit: Fortschritt als Held + Schnell-Filter. */}
          {itemCount > 0 && (
            <div className="rounded-xl border border-outline-variant/30 bg-surface-container-low p-4 dark:border-outline/30 dark:bg-surface-variant/10">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
                    {cockpit.checked} von {cockpit.total} geprüft
                  </p>
                  <p className="mt-0.5 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                    {cockpit.open > 0
                      ? `Noch ${cockpit.open} Position${cockpit.open === 1 ? "" : "en"} offen — der Katalog geht erst live, wenn alle geklärt sind.`
                      : "Alles geklärt — du kannst den Import abschließen."}
                  </p>
                </div>
                {job?.aiUsage && (
                  <span className="font-body-sm text-[12px] text-on-surface-variant/70">Analyse ≈ {(job.aiUsage.costEurCents / 100).toFixed(2)} €</span>
                )}
              </div>
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-surface-container-high dark:bg-white/[0.08]">
                <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${cockpit.total ? Math.round((cockpit.checked / cockpit.total) * 100) : 0}%` }} />
              </div>
              {(finishStats.spellN > 0 || unresolvedAllergens > 0) && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {(focusSpellCount + focusAllergenCount) > 0 && (
                    <Button className="!px-3 !py-1.5" onClick={() => openFocus("all")}>
                      <Icon name="bolt" className="text-[16px]" /> Offene klären — Schritt für Schritt
                    </Button>
                  )}
                  {finishStats.spellN > 0 && (
                    <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-secondary/40 bg-secondary/5 px-2.5 py-1 font-label-md text-[12px] font-semibold text-secondary hover:bg-secondary/10 dark:text-[#b8ae9e]" onClick={() => setReviewFilter("suggestions")}>
                      <Icon name="spellcheck" className="text-[14px]" /> {finishStats.spellN} Schreibkorrektur{finishStats.spellN === 1 ? "" : "en"} ansehen
                    </button>
                  )}
                  {unresolvedAllergens > 0 && (
                    <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-secondary/40 bg-secondary/5 px-2.5 py-1 font-label-md text-[12px] font-semibold text-secondary hover:bg-secondary/10 dark:text-[#b8ae9e]" onClick={() => setReviewFilter("open")}>
                      <Icon name="verified" className="text-[14px]" /> {unresolvedAllergens} Allergen{unresolvedAllergens === 1 ? "" : "e"} zu klären
                    </button>
                  )}
                </div>
              )}
              {/* 150 P2 (K3): von der KI erkanntes Preis-Layout der Karte, umschaltbar. */}
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-outline-variant/20 pt-3 dark:border-outline/20">
                <span className="font-body-sm text-[12px] text-on-surface-variant/70">Preis-Layout der Karte:</span>
                <div className="w-[200px]">
                  <Select
                    className="!py-1.5"
                    ariaLabel="Preis-Layout der Karte"
                    testId="preis-layout-karte"
                    value={menu.preisLayout ?? "spalte"}
                    onValueChange={(v) => setMenu((prev) => (prev ? { ...prev, preisLayout: v as PreisLayout } : prev))}
                    options={[
                      { value: "spalte", label: "Preise in Spalte" },
                      { value: "inline", label: "Preise im Text" },
                    ]}
                  />
                </div>
              </div>
              {/* Nachträglich: Rechtschreibung und Allergene jederzeit erneut von der KI prüfen lassen. */}
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-outline-variant/20 pt-3 dark:border-outline/20">
                <span className="font-body-sm text-[12px] text-on-surface-variant/70">Nachträglich prüfen:</span>
                <button type="button" disabled={enrichBusy} className="inline-flex items-center gap-1.5 rounded-full border border-secondary/40 bg-secondary/5 px-2.5 py-1 font-label-md text-[12px] font-semibold text-secondary hover:bg-secondary/10 disabled:opacity-50 dark:text-[#b8ae9e]" onClick={() => void requestSpellCheck()}>
                  <Icon name="spellcheck" className="text-[14px]" /> {enrichBusy ? "KI prüft …" : "Rechtschreibung prüfen"}
                </button>
                <button type="button" disabled={enrichBusy} className="inline-flex items-center gap-1.5 rounded-full border border-secondary/40 bg-secondary/5 px-2.5 py-1 font-label-md text-[12px] font-semibold text-secondary hover:bg-secondary/10 disabled:opacity-50 dark:text-[#b8ae9e]" onClick={() => void requestAllergenSuggestions()}>
                  <Icon name="auto_awesome" className="text-[14px]" /> {enrichBusy ? "KI fragt …" : "Allergene vorschlagen"}
                </button>
                {IMPORT_SPRACHEN.map((lang) => (
                  <button key={lang} type="button" disabled={enrichBusy} className="inline-flex items-center gap-1.5 rounded-full border border-outline-variant/50 px-2.5 py-1 font-label-md text-[12px] font-semibold text-on-surface-variant hover:bg-surface-container-high disabled:opacity-50 dark:border-outline/40" onClick={() => void requestTranslation(lang)}>
                    <Icon name="translate" className="text-[14px]" /> {SPRACH_KURZ[lang]} übersetzen
                  </button>
                ))}
                {enrichNotice && <span className="font-body-sm text-[12px] text-on-surface-variant">{enrichNotice}</span>}
              </div>
            </div>
          )}

          {/* Filter-Tabs: Alle · Offen · KI-Vorschläge · Geprüft */}
          {itemCount > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 border-b border-outline-variant/30 pb-2 dark:border-outline/30">
              {([
                { key: "all" as const, label: "Alle", n: cockpit.total },
                { key: "open" as const, label: "Offen", n: cockpit.open },
                { key: "suggestions" as const, label: "KI-Vorschläge", n: cockpit.withSug },
                { key: "checked" as const, label: "Geprüft", n: cockpit.checked },
              ]).map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setReviewFilter(t.key)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-label-md text-label-md font-semibold transition-colors ${
                    reviewFilter === t.key
                      ? "bg-primary/10 text-primary"
                      : "text-on-surface-variant hover:bg-surface-container-high/60 dark:hover:bg-surface-variant/20"
                  }`}
                >
                  {t.label} <span className={reviewFilter === t.key ? "text-primary/70" : "text-on-surface-variant/50"}>{t.n}</span>
                </button>
              ))}
              {flags.some((f) => f.level === "warn") && (
                <span className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-[#F0EAE0] px-2.5 py-1 font-label-md text-[12px] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                  <Icon name="warning" className="text-[15px]" /> Gelb markierte Felder prüfen
                </span>
              )}
            </div>
          )}

          {/* Backlog #41: Original-Dateien (R2) - nur die, fuer die tatsaechlich ein r2Key persistiert wurde. */}
          {job && job.sourceFiles.some((f) => f.r2Key) && (
            <div className="flex flex-wrap items-center gap-2 font-body-sm text-body-sm text-on-surface-variant">
              <span>Originaldateien:</span>
              {job.sourceFiles.map((f, i) =>
                f.r2Key ? (
                  <button
                    key={i}
                    type="button"
                    className="inline-flex items-center gap-1 rounded-lg bg-surface-container-high px-2.5 py-1 font-label-md text-label-md text-primary hover:underline dark:bg-white/[0.08]"
                    onClick={() => void openAuthenticatedFile(`/api/menu-imports/${job.id}/files/${i}`)}
                  >
                    <Icon name="visibility" className="text-[15px]" /> Datei {i + 1} ansehen
                  </button>
                ) : null,
              )}
            </div>
          )}

          {/* M2-Fix: Empty-State statt leerer Liste, wenn die KI keine Gerichte erkannt hat -
              inkl. zweier sichtbarer Recovery-Optionen statt einer stillen Sackgasse. */}
          {itemCount === 0 && (
            <Card>
              <EmptyState
                icon="search_off"
                title="Keine Gerichte erkannt"
                hint="In dieser Datei bzw. diesem Text wurden keine Gerichte gefunden. Erneut analysieren, eine andere Quelle wählen oder manuell im Katalog anlegen."
              >
              <div className="mt-2 flex flex-wrap justify-center gap-3">
                <Button
                  onClick={() => { setStep("upload"); setFiles([]); setText(""); setJob(null); setMenu(null); }}
                >
                  <Icon name="refresh" className="text-[18px]" /> Anderes Foto versuchen
                </Button>
                <Button variant="secondary" onClick={() => navigate("/katalog")}>
                  <Icon name="edit" className="text-[18px]" /> Manuell im Katalog anlegen
                </Button>
              </div>
              </EmptyState>
            </Card>
          )}

          {menu.sections.map((section, si) => {
            // 16a: Filter — Sektion ausblenden, wenn im aktiven Filter keine Position sichtbar ist.
            const anyVisible = section.items.some((_, ii) => itemMatchesFilter(si, ii));
            if (reviewFilter !== "all" && !anyVisible) return null;
            const secOpen = section.items.filter((_, ii) => !itemIsChecked(si, ii)).length;
            return (
            <Card key={si} className="overflow-hidden">
              <div className="border-b border-outline-variant/30 bg-surface-container-low px-5 py-3 dark:border-outline/30 dark:bg-surface-variant/10">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="flex flex-wrap items-center gap-2 font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">
                    {section.title}
                    {openSpellSuggestions()
                      .filter((g) => g.sectionIndex === si && g.itemIndex === null)
                      .map((g, k) => (
                        <SpellChip key={k} corrected={g.corrected} onApply={() => applySpell(g)} />
                      ))}
                    {section.menuPriceCents != null && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-0.5 align-middle font-label-md text-label-md text-primary">
                        <Icon name="restaurant_menu" className="text-[14px]" /> Menü · Preis p. P.
                      </span>
                    )}
                  </h3>
                  {/* 150 P2 (K3): Preis-Layout je Kategorie — erbt die Karte, solange nichts gewählt ist. */}
                  <div className="ml-auto w-[170px]">
                    <Select
                      className="!py-1.5"
                      ariaLabel={`Preis-Layout von ${section.title}`}
                      testId={`preis-layout-sektion-${si}`}
                      value={section.preisLayout ?? "erbt"}
                      onValueChange={(v) => patchSection(si, { preisLayout: v === "erbt" ? null : (v as PreisLayout) })}
                      options={[
                        { value: "erbt", label: "Preise wie Karte" },
                        { value: "spalte", label: "Preise in Spalte" },
                        { value: "inline", label: "Preise im Text" },
                      ]}
                    />
                  </div>
                  {section.menuPriceCents == null ? (
                    <Button
                      variant="ghost"
                      className="!px-2 font-label-md text-label-md text-on-surface-variant"
                      title="Diese Sektion als Menü mit einem Gesamtpreis pro Person behandeln (Gänge ohne Einzelpreise)"
                      onClick={() => patchSection(si, { menuPriceCents: 0, menuVariants: section.menuVariants ?? [] })}
                    >
                      <Icon name="restaurant_menu" className="text-[16px]" /> Als Menü mit Gesamtpreis
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      className="!px-2 font-label-md text-label-md text-on-surface-variant"
                      title="Menü auflösen: Positionen wieder als Einzelgerichte mit eigenen Preisen behandeln"
                      onClick={() => patchSection(si, { menuPriceCents: null })}
                    >
                      <Icon name="close" className="text-[16px]" /> Menü auflösen
                    </Button>
                  )}
                </div>
                {section.menuPriceCents != null && (
                  <div className="mt-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-label-md text-label-md text-on-surface-variant">Menüpreis pro Person</span>
                      <div className="relative w-[140px]">
                        <PriceInput
                          cents={section.menuPriceCents}
                          priceDisplay={section.menuPriceDisplay}
                          onCommit={(cents) => patchSection(si, { menuPriceCents: cents ?? 0 })}
                        />
                        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant">€</span>
                      </div>
                    </div>
                    {(section.menuVariants ?? []).map((variant, vi) => (
                      <div key={vi} className="flex flex-wrap items-center gap-2">
                        <Input
                          className="w-[220px]"
                          value={variant.label}
                          placeholder="Variante, z.B. mit Weinbegleitung"
                          onChange={(e) => patchVariant(si, vi, { label: e.target.value })}
                        />
                        <div className="relative w-[140px]">
                          <PriceInput cents={variant.priceCents} priceDisplay={null} onCommit={(cents) => patchVariant(si, vi, { priceCents: cents ?? 0 })} />
                          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant">€</span>
                        </div>
                        <Button
                          variant="ghost"
                          className="!px-2 text-on-surface-variant"
                          title="Variante entfernen"
                          onClick={() => removeVariant(si, vi)}
                        >
                          <Icon name="delete" className="text-[16px]" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="ghost" className="!px-2 font-label-md text-label-md text-primary" onClick={() => addVariant(si)}>
                      <Icon name="add" className="text-[16px]" /> Preis-Variante
                    </Button>
                    {(flagFor.get(`${si}:s`) ?? []).map((f, k) => (
                      <p key={k} className={`font-body-sm text-body-sm ${f.level === "warn" ? "text-secondary dark:text-[#b8ae9e]" : "text-on-surface-variant"}`}>
                        {f.level === "warn" ? "⚠ " : "ℹ "}{f.message}
                      </p>
                    ))}
                  </div>
                )}
              </div>
              <div className="divide-y divide-outline-variant/30 dark:divide-outline/30">
                {section.items.map((item, ii) => {
                  if (!itemMatchesFilter(si, ii)) return null; // 16a Filter
                  const itemFlags = (flagFor.get(`${si}:${ii}`) ?? []).filter(
                    (f) => !(section.menuPriceCents != null && f.field === "price" && f.level === "info"),
                  );
                  const warn = itemFlags.some((f) => f.level === "warn");
                  const checked = itemIsChecked(si, ii);
                  return (
                    <div key={ii} className={`p-4 ${warn ? "bg-secondary/60 dark:bg-white/[0.08]" : ""}`}>
                      {/* 16a Status-Pill: Geprüft (grün) vs. Zu prüfen (amber). */}
                      <div className="mb-2">
                        {checked ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#F0EAE0] px-2 py-0.5 font-label-md text-[11px] font-semibold text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                            <Icon name="check" className="text-[13px]" /> Geprüft
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-secondary/15 px-2 py-0.5 font-label-md text-[11px] font-semibold text-secondary dark:text-[#b8ae9e]">
                            <Icon name="pending" className="text-[13px]" /> Zu prüfen
                          </span>
                        )}
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="grid flex-1 gap-3 sm:grid-cols-[1fr_140px]">
                          <div className="space-y-2">
                            <Input
                              className="font-semibold"
                              value={item.name}
                              onChange={(e) => patchItem(si, ii, { name: e.target.value, translations: null })}
                              // E2E (speisekarten-import.spec.ts): reines <input value=.../> ohne
                              // Label/Platzhalter, keine ARIA-Rolle zum Anfassen verfuegbar.
                              data-testid={`karten-import-position-${si}-${ii}`}
                            />
                            {openSpellSuggestions()
                              .filter((g) => g.sectionIndex === si && g.itemIndex === ii && g.field === "name")
                              .map((g, k) => (
                                <SpellChip key={k} corrected={g.corrected} onApply={() => applySpell(g)} />
                              ))}
                            {/* Befund 31.07.2026, Auftrag Punkt 2: dezenter Aehnlichkeits-Hinweis -
                                REINE Anzeige, kein Klick, kein automatisches Zusammenfuehren. */}
                            {(() => {
                              const sim = similarSuggestionFor(si, ii);
                              if (!sim) return null;
                              return (
                                <p className="flex items-center gap-1.5 font-body-sm text-[12px] text-on-surface-variant/80">
                                  <Icon name="compare_arrows" className="shrink-0 text-[14px]" />
                                  Ähnlich zu bereits vorhandenem „{sim.match.name}", {centsToEuros(sim.match.priceCents)} € — prüfen?
                                </p>
                              );
                            })()}
                            <Textarea
                              className="min-h-[40px] text-body-sm"
                              placeholder="Beschreibung (optional)"
                              value={item.description ?? ""}
                              onChange={(e) => patchItem(si, ii, { description: e.target.value, translations: null })}
                            />
                            {openSpellSuggestions()
                              .filter((g) => g.sectionIndex === si && g.itemIndex === ii && g.field === "description")
                              .map((g, k) => (
                                <SpellChip key={k} corrected={g.corrected} onApply={() => applySpell(g)} />
                              ))}
                            {item.translations && (
                              <ul className="space-y-0.5 font-body-sm text-[12px] text-on-surface-variant/80">
                                {IMPORT_SPRACHEN.filter((l) => item.translations?.[l]).map((l) => (
                                  <li key={l} className="truncate" title={item.translations![l]!.description ?? undefined}>
                                    <span className="mr-1.5 font-semibold">{SPRACH_KURZ[l]}</span>{item.translations![l]!.name}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div className="space-y-2">
                            {section.menuPriceCents != null ? (
                              <div
                                className="rounded-lg bg-surface-container-high px-2.5 py-2 text-center font-body-sm text-body-sm text-on-surface-variant dark:bg-white/[0.08]"
                                title="Gänge eines Menüs tragen keinen Einzelpreis - der Preis steht am Menü."
                              >
                                Im Menüpreis
                              </div>
                            ) : (
                              <>
                                <div className="relative">
                                  <PriceInput
                                    cents={item.priceCents}
                                    priceDisplay={item.priceDisplay}
                                    onCommit={(cents) => patchItem(si, ii, { priceCents: cents })}
                                  />
                                  <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant">€</span>
                                </div>
                                {item.priceCents == null && item.priceDisplay && (
                                  <p className="text-right font-body-sm text-body-sm text-on-surface-variant/70">{item.priceDisplay}</p>
                                )}
                                {(() => {
                                  const alt = altpreisFuer(si, ii);
                                  const prozent = alt == null ? null : preissprungProzent(alt, item.priceCents);
                                  if (prozent == null) return null;
                                  return (
                                    <p data-testid={`preissprung-${si}-${ii}`} className="rounded-lg bg-secondary/10 px-2 py-1 text-right font-body-sm text-[12px] font-semibold text-secondary dark:text-[#b8ae9e]">
                                      <Icon name={prozent > 0 ? "trending_up" : "trending_down"} className="mr-1 align-[-3px] text-[14px]" />
                                      {prozent > 0 ? "+" : ""}{prozent} % ggü. bisher {centsToEuros(alt)} €
                                    </p>
                                  );
                                })()}
                              </>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          className="!px-2 text-on-surface-variant"
                          title="Position entfernen"
                          onClick={() => removeItem(si, ii)}
                        >
                          <Icon name="delete" className="text-[18px]" />
                        </Button>
                      </div>

                      {/* Allergene (Tri-State H-02: null = ungeklaert, [] = geprueft keine, Codes = geprueft) */}
                      <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        {LMIV.map(({ code, label }) => {
                          const on = item.allergens?.includes(code) ?? false;
                          return (
                            <button
                              key={code}
                              type="button"
                              title={label}
                              onClick={() =>
                                patchItem(si, ii, {
                                  allergens: on
                                    ? (item.allergens ?? []).filter((a) => a !== code)
                                    : [...(item.allergens ?? []), code],
                                })
                              }
                              className={`rounded border px-1.5 py-0.5 font-label-md text-[11px] font-semibold transition-colors ${
                                on
                                  ? "border-primary bg-primary/10 text-primary"
                                  : "border-outline-variant/50 text-on-surface-variant/60 hover:border-primary/40 dark:border-outline/40"
                              }`}
                            >
                              {code}
                            </button>
                          );
                        })}
                        {item.allergens === null && (
                          <button
                            type="button"
                            title="Diese Position hat keine erkannte Allergenangabe. Bestätigen, dass sie keine LMIV-Hauptallergene enthält — oder oben Codes anklicken."
                            onClick={() => patchItem(si, ii, { allergens: [] })}
                            className="rounded border border-secondary/60 bg-secondary/10 px-2 py-0.5 font-label-md text-[11px] font-semibold text-secondary transition-colors hover:bg-secondary/20 dark:text-[#b8ae9e]"
                          >
                            Ungeklärt — „keine Allergene" bestätigen
                          </button>
                        )}
                        <span className="mx-1 w-px bg-outline-variant/40" />
                        {DIET.map(({ key, label }) => {
                          const on = item.dietFlags.includes(key);
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() =>
                                patchItem(si, ii, {
                                  dietFlags: on ? item.dietFlags.filter((d) => d !== key) : [...item.dietFlags, key],
                                })
                              }
                              className={`rounded border px-2 py-0.5 font-label-md text-[11px] transition-colors ${
                                on
                                  ? "border-outline bg-secondary/10 text-secondary dark:text-[#b8ae9e]"
                                  : "border-outline-variant/50 text-on-surface-variant/60 hover:border-secondary/40 dark:border-outline/40"
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>

                      {/* LMIV-Allergen-Vorschlag der KI (nur bei ungeklaerter Position). Immer
                          Vorschlag - EIN Klick uebernimmt, nie automatisch. Ersetzt keine
                          Rechtsberatung; die tatsaechliche Rezeptur kennt nur der Betrieb. */}
                      {(() => {
                        const asug = allergenSuggestionFor(si, ii);
                        if (!asug) return null;
                        return (
                          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-secondary/40 bg-secondary/5 px-2.5 py-1.5 dark:border-white/10">
                            <Icon name="verified" className="shrink-0 text-[15px] text-secondary dark:text-[#b8ae9e]" />
                            <span className="font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
                              <span className="font-semibold text-secondary dark:text-[#b8ae9e]">KI-Vorschlag: </span>
                              {asug.codes.map((c) => `${c} · ${lmivLabel(c)}`).join(", ")}
                              {asug.reason ? <span className="text-on-surface-variant/80"> — {asug.reason}</span> : null}
                            </span>
                            <button
                              type="button"
                              className="ml-auto inline-flex items-center gap-1 rounded-full border border-secondary/50 bg-secondary/10 px-2.5 py-0.5 font-label-md text-[11px] font-semibold text-secondary transition-colors hover:bg-secondary/20 dark:text-[#b8ae9e]"
                              title="Vorgeschlagene LMIV-Allergene übernehmen. KI-Vorschlag auf Basis typischer Rezepturen — ersetzt keine Rechtsberatung und nicht die Kenntnis der tatsächlichen Zutaten. Bitte prüfen."
                              onClick={() => applyAllergen(asug)}
                            >
                              <Icon name="add" className="text-[13px]" /> Übernehmen
                            </button>
                          </div>
                        );
                      })()}

                      {/* Sanity-Hinweise */}
                      {itemFlags.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {itemFlags.map((f, k) => (
                            <p
                              key={k}
                              className={`flex items-center gap-1.5 font-body-sm text-body-sm ${
                                f.level === "warn" ? "text-secondary dark:text-[#b8ae9e]" : "text-on-surface-variant/70"
                              }`}
                            >
                              <Icon name={f.level === "warn" ? "warning" : "info"} className="text-[14px]" />
                              {f.message}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
            );
          })}

          {/* 16a: Filter aktiv, aber keine Position sichtbar. */}
          {itemCount > 0 && reviewFilter !== "all" && cockpit.total > 0 && (
            (reviewFilter === "open" && cockpit.open === 0) ||
            (reviewFilter === "checked" && cockpit.checked === 0) ||
            (reviewFilter === "suggestions" && cockpit.withSug === 0)
          ) && (
            <Card className="flex flex-col items-center gap-2 py-10 text-center">
              <Icon name="filter_alt_off" className="text-3xl text-on-surface-variant/50" />
              <p className="font-body-sm text-body-sm text-on-surface-variant">Keine Position in diesem Filter.</p>
              <Button variant="secondary" onClick={() => setReviewFilter("all")}>Alle anzeigen</Button>
            </Card>
          )}

          {/* P1 (Speisekarten): Ziel-Karte des Imports — bewusst HIER im Review (der Betreiber
              sieht erst, WAS erkannt wurde, und entscheidet dann, wohin es gehört). */}
          {itemCount > 0 && (
            <div className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-4 dark:border-outline/30 dark:bg-inverse-surface">
              <h4 className="mb-3 flex items-center gap-2 font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                <Icon name="menu_book" className="text-[16px]" /> Ziel-Karte
              </h4>
              {!aufforderungWeg && (
                <div className="mb-3">
                  <KartenartAufforderung ohneArt={existingCards.filter((k) => !k.cardTypeId).length} onWeg={() => setAufforderungWeg(true)} />
                </div>
              )}
              <div className="mb-4 border-b border-outline-variant/30 pb-4">
                <KartenartWahl
                  types={cardTypes}
                  cards={existingCards}
                  cardTypeId={cardTypeId}
                  suggestion={kiKartenart}
                  antwort={kartenartAntwort}
                  replaceCardId={appendCardId}
                  onCardType={(id) => waehleKartenart(id)}
                  onAntwort={beantworteKartenart}
                  onReplaceCard={(id) => {
                    setCardMode("replace");
                    setAppendCardId(id);
                  }}
                  onCreateType={neueKartenart}
                />
              </div>
              {/* Uebersetzungs-Erkennung (Preis-Fingerprint): nur ein VORSCHLAG mit Ein-Klick-
                  Uebernahme - Radios darunter bleiben immer frei uebersteuerbar. */}
              {translationCandidate && !detectionDismissed && (
                <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5">
                  <Icon name="translate" className="text-[18px] text-primary" />
                  <span className="min-w-0 flex-1 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                    <span className="mr-1.5 rounded bg-primary px-1.5 py-0.5 font-label-md text-[11px] font-bold uppercase tracking-wide text-on-primary">KI-Empfehlung</span>
                    Diese Karte scheint die {LANG_NAME[job?.sourceLang ?? ""] ?? "fremdsprachige"} Version von{" "}
                    <strong>„{translationCandidate.card.name}"</strong> zu sein ({Math.round(translationCandidate.score * 100)} %
                    Preisübereinstimmung). <strong>Empfohlen: als Übersetzung zuordnen.</strong>
                  </span>
                  <span className="flex shrink-0 gap-2">
                    <Button
                      className="!px-3 !py-1.5"
                      disabled={matchBusy}
                      onClick={() => void startTranslationMatch(translationCandidate.card, translateLang)}
                    >
                      {matchBusy ? "Ordnet zu …" : "Als Übersetzung zuordnen"}
                    </Button>
                    <Button variant="secondary" className="!px-3 !py-1.5" onClick={() => setDetectionDismissed(true)}>
                      Nein, eigenständige Karte
                    </Button>
                  </span>
                </div>
              )}
              {(!translationCandidate || detectionDismissed) && job?.sourceLang && job.sourceLang !== "de" && existingCards.length > 0 && (
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-surface-container-high/50 px-3 py-2.5 dark:bg-surface-variant/20">
                  <span className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                    <Icon name="translate" className="mr-1 align-[-3px] text-[15px]" />
                    Fremdsprachige Karte ({(job.sourceLang ?? "").toUpperCase()})? Als Übersetzung einer bestehenden Karte
                    zuordnen:
                  </span>
                  <Select
                    ariaLabel="Bestehende Karte"
                    className="max-w-[260px] !py-1.5"
                    value={appendCardId}
                    onValueChange={setAppendCardId}
                    placeholder="Karte wählen …"
                    options={existingCards.map((k) => ({ value: k.id, label: `${k.name} (${k.itemCount ?? 0} Positionen)` }))}
                  />
                  <Select
                    ariaLabel="Sprache dieser Karte"
                    className="max-w-[150px] !py-1.5"
                    value={translateLang}
                    onValueChange={setTranslateLang}
                    options={[
                      { value: "en", label: "Englisch" },
                      { value: "it", label: "Italienisch" },
                      { value: "fr", label: "Französisch" },
                      { value: "es", label: "Spanisch" },
                    ]}
                  />
                  <Button
                    variant="secondary"
                    className="!px-3 !py-1.5"
                    disabled={matchBusy || !appendCardId}
                    onClick={() => {
                      const card = existingCards.find((k) => k.id === appendCardId);
                      if (card) void startTranslationMatch(card, translateLang);
                    }}
                  >
                    {matchBusy ? "Ordnet zu …" : "Zuordnen"}
                  </Button>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <label className="flex cursor-pointer items-center gap-2 font-body-sm text-body-sm">
                  <Radio name="cardMode" checked={cardMode === "create"} onChange={() => setCardMode("create")} />
                  Neue Karte anlegen
                </label>
                <label className={`flex items-center gap-2 font-body-sm text-body-sm ${existingCards.length === 0 ? "opacity-40" : "cursor-pointer"}`}>
                  <Radio name="cardMode" disabled={existingCards.length === 0} checked={cardMode === "append"} onChange={() => setCardMode("append")} />
                  Bestehende Karte ergänzen
                </label>
                <label className={`flex items-center gap-2 font-body-sm text-body-sm ${existingCards.length === 0 ? "opacity-40" : "cursor-pointer"}`}>
                  <Radio name="cardMode" disabled={existingCards.length === 0} checked={cardMode === "replace"} onChange={() => setCardMode("replace")} />
                  Bestehende Karte ersetzen
                </label>
                <label className="flex cursor-pointer items-center gap-2 font-body-sm text-body-sm">
                  <Radio name="cardMode" checked={cardMode === "none"} onChange={() => setCardMode("none")} />
                  Ohne Karte
                </label>
              </div>
              {cardMode === "create" && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Input className="max-w-[340px]" placeholder="Name der Karte, z. B. Mittagskarte" value={cardName} onChange={(e) => setCardName(e.target.value)} />
                  <div className="flex items-center gap-2" title="Das Sortiment bestimmt Art und MwSt. neuer Positionen (Getränkekarte → Getränk, 19 %).">
                    <span className="font-body-sm text-body-sm text-on-surface-variant">Sortiment</span>
                    <div className="max-w-[200px]">
                    <Select
                      value={cardKind}
                      onValueChange={(v) => setCardKind(v as "food" | "drinks" | "mixed")}
                      ariaLabel="Sortiment – bestimmt Art und MwSt. neuer Positionen (Getränkekarte → Getränk, 19 %)."
                      options={[
                        { value: "food", label: "Speisekarte" },
                        { value: "drinks", label: "Getränkekarte" },
                        { value: "mixed", label: "Gemischt (Speisen + Getränke)" },
                      ]}
                    />
                    </div>
                  </div>
                </div>
              )}
              {(cardMode === "append" || cardMode === "replace") && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <div className="max-w-[340px]">
                    <Select
                      value={appendCardId || KEINE_KARTE}
                      onValueChange={(v) => setAppendCardId(v === KEINE_KARTE ? "" : v)}
                      options={[
                        { value: KEINE_KARTE, label: "Karte wählen …" },
                        ...existingCards.map((k) => ({ value: k.id, label: `${k.name} (${k.itemCount ?? 0} Positionen)` })),
                      ]}
                    />
                  </div>
                </div>
              )}
              {cardMode === "replace" && (
                <p className="mt-2 rounded-lg bg-error/5 px-3 py-2 font-body-sm text-body-sm text-error">
                  <Icon name="warning" className="mr-1 align-[-3px] text-[15px]" />
                  Ersetzen archiviert alle Positionen und Kategorien dieser Karte, die im neuen Import fehlen.
                  Angebote und Pakete behalten ihre Referenzen; betroffene Pakete werden markiert.
                </p>
              )}
              <p className="mt-2 font-body-sm text-[12px] text-on-surface-variant/70">
                Karten gruppieren den Katalog (Mittags-/Abend-/Getränkekarte) und lassen sich später einzeln erneuern.
              </p>
            </div>
          )}

          {/* Commit-Leiste — M2-Fix: bei 0 Positionen ausgeblendet, der Empty-State übernimmt. */}
          {itemCount > 0 && (
          <div className="sticky bottom-0 z-10 space-y-3 rounded-xl border border-outline-variant/30 bg-surface-container-lowest/95 p-4 shadow-lg backdrop-blur dark:border-outline/30 dark:bg-inverse-surface/95">
            {/* Uebersetzungs-Angebot IM Hauptbalken - sonst wird es beim Durchscrollen uebersehen
                und die EN-Karte landet als 80+ Duplikate im Katalog. */}
            {translationCandidate && !detectionDismissed && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2.5">
                <Icon name="translate" className="text-[18px] text-primary" />
                <span className="min-w-0 flex-1 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                  <span className="mr-1.5 rounded bg-primary px-1.5 py-0.5 font-label-md text-[11px] font-bold uppercase tracking-wide text-on-primary">KI-Empfehlung</span>
                  Wahrscheinlich die {LANG_NAME[job?.sourceLang ?? ""] ?? "fremdsprachige"} Version von „{translationCandidate.card.name}" ({Math.round(translationCandidate.score * 100)} % Preisübereinstimmung).{" "}
                  <strong>Empfohlen: als Übersetzung zuordnen — nicht neu importieren</strong> (sonst entstehen {itemCount} Duplikat-Positionen).
                </span>
                <span className="flex shrink-0 gap-2">
                  <Button className="!px-3 !py-1.5" disabled={matchBusy} onClick={() => void startTranslationMatch(translationCandidate.card, translateLang)}>
                    {matchBusy ? "Ordnet zu …" : "Als Übersetzung zuordnen"}
                  </Button>
                  <Button variant="secondary" className="!px-3 !py-1.5" onClick={() => setDetectionDismissed(true)}>
                    Nein, eigenständige Karte
                  </Button>
                </span>
              </div>
            )}
            {!translationCandidate && job?.sourceLang && job.sourceLang !== "de" && existingCards.length > 0 && (
              <p className="rounded-lg bg-surface-container-high/60 px-3 py-2 font-body-sm text-body-sm text-on-surface-variant dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
                <Icon name="translate" className="mr-1 align-[-3px] text-[15px]" />
                Fremdsprachige Karte ({(job.sourceLang ?? "").toUpperCase()}) — Übersetzung einer bestehenden Karte? Im Block „Ziel-Karte" oben zuordnen statt neu importieren.
              </p>
            )}
            {/* EINE Stelle fuer alle KI-Vorschlaege: ein Klick uebernimmt Rechtschreibung UND
                Allergene. Erscheint nur, wenn etwas offen ist; Aufschluesselung transparent.
                Einzeln geht weiterhin direkt am Feld (Chips). */}
            {(() => {
              const nSpell = openSpellSuggestions().length;
              const nAllergen = openAllergenSuggestions().length;
              const total = nSpell + nAllergen;
              if (total === 0) return null;
              const parts = [
                nSpell > 0 ? `${nSpell}× Rechtschreibung` : null,
                nAllergen > 0 ? `${nAllergen}× Allergene` : null,
              ].filter(Boolean).join(" · ");
              return (
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-secondary/40 bg-secondary/5 px-3 py-2.5 dark:border-white/10">
                  <Icon name="auto_awesome" className="text-[18px] text-secondary dark:text-[#b8ae9e]" />
                  <span className="min-w-0 flex-1 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                    <strong>{total} KI-Vorschläge</strong> offen <span className="text-on-surface-variant/80">({parts})</span>
                  </span>
                  <Button
                    className="!px-3 !py-1.5"
                    title="Übernimmt alle offenen KI-Vorschläge auf einmal: Rechtschreib-Korrekturen und Allergen-Vorschläge. Jeder Vorschlag ist auch einzeln am Feld übernehmbar. Allergene ersetzen keine Rechtsberatung — bitte prüfen."
                    onClick={applyAllSuggestions}
                  >
                    Alle {total} übernehmen
                  </Button>
                </div>
              );
            })()}
            {/* Teil-Commit-Banner: was live ging, was als Entwurf blieb. */}
            {partialNotice !== null && partialNotice > 0 && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-secondary/40 bg-secondary/5 px-3 py-2.5 dark:border-white/10">
                <Icon name="check_circle" className="text-[18px] text-secondary dark:text-[#b8ae9e]" />
                <span className="min-w-0 flex-1 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                  Geprüfte Positionen sind live. <strong>{partialNotice} als Entwurf behalten</strong> — klär sie jetzt oder schließ den Import später erneut ab.
                </span>
                <Button variant="ghost" className="!px-2 text-on-surface-variant" title="Hinweis ausblenden" onClick={() => setPartialNotice(null)}>
                  <Icon name="close" className="text-[16px]" />
                </Button>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="font-body-sm text-body-sm">
                {unresolvedAllergens > 0 ? (
                  <span className="font-semibold text-secondary dark:text-[#b8ae9e]">
                    <Icon name="warning" className="mr-1 align-[-3px] text-[15px]" />
                    {unresolvedAllergens} Position{unresolvedAllergens === 1 ? "" : "en"} mit ungeklärten Allergenen
                  </span>
                ) : (
                  <span className="font-semibold text-secondary dark:text-[#b8ae9e]">
                    <Icon name="check_circle" className="mr-1 align-[-3px] text-[15px]" />
                    Alle Allergene geklärt — bereit zum Abschließen
                  </span>
                )}
              </p>
              <div className="flex flex-wrap gap-3">
                <Button variant="secondary" onClick={() => navigate("/katalog")}>
                  Abbrechen
                </Button>
                {cockpit.open > 0 && (
                  <Button variant="secondary" onClick={() => openFocus("all")}>
                    <Icon name="bolt" className="text-[16px]" /> Offene einzeln klären
                  </Button>
                )}
                <Button
                  disabled={itemCount === 0 || busy}
                  onClick={() => { setDraftChoice("draft"); setAllergensConfirmed(false); setFinishOpen(true); }}
                >
                  Import abschließen
                </Button>
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      {/* 16a Undo-Toast: nach einer Sammel-Aktion (alle Vorschläge übernommen) rückgängig machen. */}
      {undoState && step === "review" && (
        <div className="fixed bottom-24 left-1/2 z-[65] flex -translate-x-1/2 items-center gap-3 rounded-full border border-outline-variant/40 bg-inverse-surface px-4 py-2.5 text-inverse-on-surface shadow-xl dark:border-outline/30 dark:bg-surface-container-high">
          <Icon name="check_circle" className="text-[18px] text-secondary-fixed-dim" />
          <span className="font-body-sm text-body-sm">{undoState.label}</span>
          <button type="button" className="font-label-md text-label-md font-semibold text-primary hover:underline" onClick={undoLast}>
            Rückgängig
          </button>
          <button type="button" className="text-inverse-on-surface/60 hover:text-inverse-on-surface" title="Schließen" onClick={() => setUndoState(null)}>
            <Icon name="close" className="text-[16px]" />
          </button>
        </div>
      )}

      {/* -------- 16b Fokus-Klären: tastaturgesteuerte Queue, Position für Position -------- */}
      {focusOpen && menu && (
        <div className="fixed inset-0 z-[75] flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-[2px]" onClick={() => setFocusOpen(false)}>
          <div className="mt-[5vh] w-full max-w-xl rounded-2xl border border-outline-variant/40 bg-surface-container-lowest shadow-2xl dark:border-outline/30 dark:bg-inverse-surface" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const q = focusQueue;
              const cur = q.length ? q[focusPos % q.length]! : null;
              const done = Math.max(0, focusBaseline - q.length);
              const nextTask = q.length > 1 ? q[(focusPos + 1) % q.length]! : null;
              const describe = (t: FocusTask): string =>
                t.kind === "spell"
                  ? `Rechtschreibung · ${t.sug.original} → ${t.sug.corrected}`
                  : `Allergene · ${menu.sections[t.si]?.items[t.ii]?.name ?? ""}`;
              return (
                <>
                  {/* Kopf */}
                  <div className="flex items-center justify-between gap-3 border-b border-outline-variant/30 px-5 py-3.5 dark:border-outline/30">
                    <div>
                      <h2 className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">Offene klären</h2>
                      <p className="font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
                        {cur ? `Position ${done + 1} von ${focusBaseline} · ${done} erledigt` : "Alles geklärt"}
                      </p>
                    </div>
                    <Button variant="ghost" className="!px-2 text-on-surface-variant" onClick={() => setFocusOpen(false)}>
                      <Icon name="close" className="text-[18px]" /> Schließen
                    </Button>
                  </div>
                  {/* Tabs */}
                  <div className="flex flex-wrap gap-1.5 border-b border-outline-variant/30 px-5 py-2.5 dark:border-outline/30">
                    {([
                      { key: "all" as const, label: "Alle", n: focusSpellCount + focusAllergenCount },
                      { key: "spell" as const, label: "Rechtschreibung", n: focusSpellCount },
                      { key: "allergen" as const, label: "Allergene", n: focusAllergenCount },
                    ]).map((t) => (
                      <button key={t.key} type="button" onClick={() => setFocusTab(t.key)}
                        className={`rounded-lg px-2.5 py-1 font-label-md text-label-md font-semibold transition-colors ${focusTab === t.key ? "bg-primary/10 text-primary" : "text-on-surface-variant hover:bg-surface-container-high/60 dark:hover:bg-surface-variant/20"}`}>
                        {t.label} <span className="opacity-60">{t.n}</span>
                      </button>
                    ))}
                  </div>

                  {/* Körper — geteilter Karten-Inhalt (focusTaskBody). */}
                  <div className="px-5 py-5">
                    {!cur ? (
                      <div className="flex flex-col items-center gap-3 py-8 text-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F0EAE0] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"><Icon name="check" className="text-2xl" /></div>
                        <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Alles geklärt in diesem Filter.</p>
                        <Button onClick={() => setFocusOpen(false)}>Fertig</Button>
                      </div>
                    ) : (
                      focusTaskBody(cur)
                    )}
                  </div>

                  {/* Fusszeile: Tastatur-Hinweise + Überspringen + Als Nächstes */}
                  {cur && (
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-outline-variant/30 px-5 py-3 dark:border-outline/30">
                      <span className="font-body-sm text-[12px] text-on-surface-variant/70">
                        <kbd className="rounded bg-surface-container-high px-1 dark:bg-white/[0.08]">↵</kbd> Übernehmen · <kbd className="rounded bg-surface-container-high px-1 dark:bg-white/[0.08]">K</kbd> Keine · <kbd className="rounded bg-surface-container-high px-1 dark:bg-white/[0.08]">→</kbd> Überspringen
                      </span>
                      <span className="flex items-center gap-3">
                        {nextTask && <span className="hidden font-body-sm text-[12px] text-on-surface-variant/60 sm:inline">Als Nächstes: {describe(nextTask)}</span>}
                        <Button variant="ghost" className="text-on-surface-variant" onClick={focusSkip}>Überspringen →</Button>
                      </span>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* -------- 16c Abschluss-Dialog: Bulk-Zusammenfassung + Umgang mit offenen Positionen -------- */}
      {finishOpen && menu && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-4 backdrop-blur-[2px]" onClick={() => !busy && setFinishOpen(false)}>
          <div
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-outline-variant/40 bg-surface-container-lowest p-6 shadow-2xl dark:border-outline/30 dark:bg-inverse-surface"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">Import abschließen</h2>
            <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
              MAESTRO wendet deine Prüfung an. Das passiert:
            </p>

            {/* Was passiert — gruppierte Bulk-Zusammenfassung */}
            <ul className="mt-4 space-y-2">
              {finishStats.allergenN > 0 && (
                <li className="flex items-center gap-2.5 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                  <Icon name="verified" className="text-[17px] text-secondary dark:text-[#b8ae9e]" />
                  <span><strong>{finishStats.allergenN}</strong> Allergen-Vorschläge übernehmen</span>
                </li>
              )}
              {finishStats.spellN > 0 && (
                <li className="flex items-center gap-2.5 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                  <Icon name="spellcheck" className="text-[17px] text-secondary dark:text-[#b8ae9e]" />
                  <span><strong>{finishStats.spellN}</strong> Schreibkorrekturen übernehmen</span>
                </li>
              )}
              {finishStats.restNull > 0 && draftChoice === "confirm" && (
                <li className="flex items-center gap-2.5 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                  <Icon name="playlist_add_check" className="text-[17px] text-on-surface-variant" />
                  <span><strong>{finishStats.restNull}</strong> Positionen als „keine Allergene" bestätigen</span>
                </li>
              )}
              <li className="flex items-center gap-2.5 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                <Icon name="restaurant_menu" className="text-[17px] text-on-surface-variant" />
                <span>
                  <strong>
                    {draftChoice === "draft" && finishStats.restNull > 0 ? itemCount - finishStats.restNull : itemCount}
                  </strong>{" "}
                  Positionen in den Katalog übernehmen
                </span>
              </li>
            </ul>

            {/* Umgang mit noch offenen Positionen (ohne Vorschlag) */}
            {finishStats.restNull > 0 && (
              <div className="mt-5 rounded-xl border border-secondary/40 bg-secondary/5 p-4 dark:border-white/10">
                <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                  {finishStats.restNull} Position{finishStats.restNull === 1 ? "" : "en"} noch offen
                </p>
                <p className="mt-0.5 font-body-sm text-[12px] text-on-surface-variant/80">
                  Diese haben keinen KI-Vorschlag und wurden noch nicht geprüft. Wie sollen wir damit umgehen?
                </p>
                <div className="mt-3 space-y-2">
                  {[
                    { key: "draft" as const, icon: "drafts", title: "Als Entwurf behalten (nicht veröffentlicht)", body: `Die ${finishStats.restNull} Gerichte bleiben privat, bis du sie prüfst. Empfohlen.` },
                    { key: "confirm" as const, icon: "check", title: "Auch als „keine Allergene“ bestätigen", body: "Schneller — aber du übernimmst die LMIV-Verantwortung ungeprüft." },
                    { key: "clarify" as const, icon: "edit", title: "Zurück und einzeln klären", body: "Schließt den Dialog — du klärst die offenen Positionen direkt in der Liste." },
                  ].map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => setDraftChoice(o.key)}
                      className={`flex w-full items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                        draftChoice === o.key
                          ? "border-primary bg-primary/5"
                          : "border-outline-variant/50 hover:border-primary/40 dark:border-outline/40"
                      }`}
                    >
                      <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${draftChoice === o.key ? "border-primary" : "border-outline-variant"}`}>
                        {draftChoice === o.key && <span className="h-2 w-2 rounded-full bg-primary" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{o.title}</span>
                        <span className="block font-body-sm text-[12px] text-on-surface-variant/80">{o.body}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* LMIV-Bestätigung — bindend */}
            {draftChoice !== "clarify" && (
              <label className="mt-5 flex cursor-pointer items-start gap-2 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                <Checkbox className="mt-0.5" checked={allergensConfirmed} onChange={(e) => setAllergensConfirmed(e.target.checked)} />
                <span>Ich bestätige, dass <strong>Allergene (LMIV)</strong> und Preise korrekt sind. Sie sind rechtlich verbindlich für den Betrieb.</span>
              </label>
            )}

            {error && <p className="mt-3 font-body-sm text-body-sm text-error dark:text-[#e39b92]"><Icon name="error" className="mr-1 align-[-3px] text-[15px]" />{error}</p>}

            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <Button variant="secondary" disabled={busy} onClick={() => setFinishOpen(false)}>Abbrechen</Button>
              {draftChoice === "clarify" ? (
                <Button onClick={() => { setFinishOpen(false); openFocus("all"); }}>Offene klären</Button>
              ) : (
                <Button
                  disabled={busy || !allergensConfirmed}
                  onClick={() => void confirmFinish()}
                >
                  {busy
                    ? "Übernimmt …"
                    : `${draftChoice === "draft" && finishStats.restNull > 0 ? itemCount - finishStats.restNull : itemCount} Positionen übernehmen`}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* -------- Fertig (Design 15e): Erfolgs-Screen mit Ergebnis-Zeilen + zwei Weiter-Wegen -------- */}
      {step === "done" && summary && (
        <div className="mx-auto max-w-xl space-y-4">
          <div className="flex flex-col items-center gap-3 pt-2 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F0EAE0] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
              <Icon name="check" className="text-3xl" />
            </div>
            <h2 className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
              Import abgeschlossen
            </h2>
            <p className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
              {summary.mode === "translate"
                ? `${summary.translatedItems ?? 0} Übersetzungen sind jetzt auf der Karte gespeichert.`
                : `${summary.createdItems + summary.updatedItems} Positionen sind jetzt in deinem Katalog verfügbar.`}
            </p>
          </div>

          {/* Ergebnis-Zeilen: was & wohin uebernommen wurde. */}
          <Card className="divide-y divide-outline-variant/20 p-0 dark:divide-outline/20">
            {summary.mode === "translate" ? (
              <>
                <ResultRow
                  icon="translate"
                  title={`${summary.translatedItems ?? 0} als ${LANG_LABEL_DONE[summary.lang ?? ""] ?? (summary.lang ?? "").toUpperCase() + "-"} Übersetzung verknüpft`}
                  sub={`Karte „${existingCards.find((k) => k.id === summary.cardId)?.name ?? "?"}“ · ${(summary.lang ?? "").toUpperCase()}`}
                />
                {(summary.skipped ?? 0) > 0 && (
                  <ResultRow icon="remove_circle_outline" title={`${summary.skipped} ohne Zuordnung übersprungen`} sub="Diese Positionen bleiben unübersetzt — jederzeit im Katalog nachpflegbar." />
                )}
                <ResultRow icon="verified" title="Preise, Allergene und Struktur unverändert" sub="Es wurden ausschließlich Texte übernommen." />
              </>
            ) : (
              <>
                <ResultRow
                  icon="menu_book"
                  title={`${summary.createdItems} neue Positionen angelegt${summary.updatedItems > 0 ? `, ${summary.updatedItems} aktualisiert` : ""}`}
                  sub={
                    cardMode === "create" && cardName.trim()
                      ? `Karte „${cardName.trim()}“ angelegt${summary.createdCategories > 0 ? ` · ${summary.createdCategories} Kategorien` : ""}`
                      : (cardMode === "append" || cardMode === "replace") && appendCardId
                        ? `Karte „${existingCards.find((k) => k.id === appendCardId)?.name ?? "?"}“ ${cardMode === "replace" ? "ersetzt" : "ergänzt"}`
                        : summary.createdCategories > 0
                          ? `In ${summary.createdCategories} neuen Kategorien einsortiert`
                          : "Im Katalog einsortiert"
                  }
                />
                {(summary.createdPackages ?? 0) > 0 && (
                  <ResultRow icon="restaurant_menu" title={`${summary.createdPackages} ${summary.createdPackages === 1 ? "Menü-Paket" : "Menü-Pakete"} angelegt`} sub="Menüs mit Gesamtpreis aus der Karte." />
                )}
                {((summary.archivedItems ?? 0) > 0 || (summary.archivedCategories ?? 0) > 0) && (
                  <ResultRow icon="archive" title={`${summary.archivedItems ?? 0} Positionen archiviert`} sub={`${summary.archivedCategories ?? 0} Kategorien archiviert — Angebote und Pakete behalten ihre Referenzen.`} />
                )}
                <ResultRow icon="verified" title="Alle Allergene (LMIV) bestätigt" sub="Rechtlich verbindlich für deinen Betrieb." />
              </>
            )}
          </Card>

          {/* §5.7: die Rueckmeldung ueber preislose Positionen — nur beim echten Katalog-Commit,
              nicht beim Uebersetzungs-Commit (dort werden ausschliesslich Texte uebernommen). */}
          {summary.mode !== "translate" && (
            <ImportPreisHinweis
              zeroPriceCount={summary.zeroPriceCount}
              zeroPriceCoveredCount={summary.zeroPriceCoveredCount}
              sectionsWithoutPrice={summary.sectionsWithoutPrice}
            />
          )}

          {summary.mode === "translate" && (summary.priceMismatches ?? 0) > 0 && (
            <p className="rounded-lg bg-secondary/10 px-3 py-2 text-center font-body-sm text-body-sm font-semibold text-secondary dark:text-[#b8ae9e]">
              {summary.priceMismatches} Preisabweichung{summary.priceMismatches === 1 ? "" : "en"} zwischen den Sprachversionen — bitte prüfen.
            </p>
          )}

          <Button className="w-full justify-center !py-3" onClick={() => navigate(summary.mode === "translate" ? "/katalog" : "/katalog?source=ai_import")}>
            Zum Katalog <Icon name="arrow_forward" className="text-[16px]" />
          </Button>
          <Button
            variant="secondary"
            className="w-full justify-center"
            onClick={() => { setStep("upload"); setFiles([]); setText(""); setJob(null); setMenu(null); setSummary(null); setAllergensConfirmed(false); setCardMode("create"); setCardName(""); setCardKind("food"); setAppendCardId(""); setPairReview(null); setDetectionDismissed(false); setGateChoice(preselectCardId ? "standalone" : "pending"); setSpellSugs([]); setAllergenSugs([]); setSimilaritySugs([]); setGateManualCardId(""); setFinishOpen(false); setDraftChoice("draft"); setPartialNotice(null); setUndoState(null); setReviewFilter("all"); setFocusOpen(false); setReviewPhase("list"); setGuidedPos(0); setNameMatchCard(null); setNameMatchResolved(false); }}
          >
            <Icon name="add" className="text-[16px]" /> Weitere Karte importieren
          </Button>
        </div>
      )}

      {/* Befund 31.07.2026 (docs/BACKLOG.md), Auftrag Punkt 1: Namens-Treffer-Nachfrage VOR dem
          KI-Parse-Lauf. "Ersetzen und vorher fragen" (Antoines Entscheidung 31.07./01.08.2026) -
          kein stilles Ueberschreiben, aber auch nicht mehr `create` als stiller Default, sobald
          eine gleichnamige, nicht archivierte Karte existiert. */}
      <Modal
        open={nameMatchCard != null}
        onClose={() => setNameMatchCard(null)}
        width="440px"
        title={<>Karte „{nameMatchCard?.name}“ gibt es schon</>}
        footer={
          <>
            <Button variant="secondary" onClick={() => resolveNameMatch("create")}>
              Als neue Karte anlegen
            </Button>
            <Button onClick={() => resolveNameMatch("replace")}>
              <Icon name="sync_alt" className="text-[16px]" /> Ersetzen (alte archivieren)
            </Button>
          </>
        }
      >
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          Es gibt bereits eine Karte „{nameMatchCard?.name}“
          {nameMatchCard?.itemCount != null ? ` (${nameMatchCard.itemCount} Positionen)` : ""} — ersetzen (die alte
          Version wird archiviert) oder als neue, zusätzliche Karte anlegen?
        </p>
      </Modal>
    </>
  );
}
